import React, { useState, useEffect, useCallback, useRef } from "react";
import * as SubframeCore from "@subframe/core";
import {
  DialogLayout,
  IconWithBackground,
  Button,
  Badge,
  DropdownMenu,
  Checkbox,
} from "../ui";
import {
  FeatherCreditCard,
  FeatherCalendar,
  FeatherActivity,
  FeatherChevronDown,
  FeatherDollarSign,
  FeatherBuilding,
  FeatherPlus,
  FeatherLoader,
} from "@subframe/core";
import {
  Transaction,
  fetchTDSRates,
  TDSRate,
  categorizeCustomerTransfer,
  categorizeInterestIncome,
  categorizeOtherIncome,
  categorizeCustomerDeposit,
  categorizeCustomerDepositSplit,
  categorizeOwnersContribution,
  categorizeVendorPaymentRefund,
  categorizeCreditsOrRefundReceived,
  categorizeCustomerExpenseRefund,
  fetchTransactionActivityLogs,
  TransactionActivityLog,
  journalsAPI,
} from "../services/api";
import {
  categorizeTransaction,
  categorizeMultipleCustomersTransaction,
} from "../services/customerApi";
import { CustomerInvoice } from "../types";
import CustomerPaymentReco from "./CustomerPaymentReco";
import CustomerTransferFromAccountReco from "./CustomerTransferFromAccountReco";
import CustomerInterestIncomeReco from "./CustomerInterestIncomeReco";
import CustomerOtherIncomeReco from "./CustomerOtherIncomeReco";
import CustomerDepositFromOtherAccountsReco from "./CustomerDepositFromOtherAccountsReco";
import CustomerDepositFromOtherAccountsSplitReco, {
  SplitItem,
} from "./CustomerDepositFromOtherAccountsSplitReco";
import CustomerPaymentOwnersContributionReco from "./CustomerPaymentOwnersContributionReco";
import CustomerVendorPaymentReco from "./CustomerVendorPaymentRefundReco";
import CustomerCreditsOrRefundReceivedReco from "./CustomerCreditsOrRefundReceivedReco";
import { BankAccount } from "../services/api";
import {
  useToast,
  ErrorBanner,
  ClosedMonthBanner,
  ClosedMonthConfirmModal,
  BankGlAccountDropdown,
  SplitTransactionAmountReviewView,
  isSplitTransactionAmountReview,
} from "../shared/presentation/components";
import {
  useErrorHandling,
  useIsMonthClosed,
  useIsAdminOrCFO,
} from "../shared/presentation/hooks";
import { dateToMonthYear } from "../shared/utils/date";
import {
  calculateSingleForeignPaymentForex,
  roundTo2Decimals,
} from "../utils/foreignPaymentForex";
import {
  ChangeDetails,
  ACTION_TYPE_MONTH_CLOSE,
} from "../shared/utils/reviewWorkflow";
import { getStoredAccountingBackend } from "../config/accounting";

// Permissions
import { usePermissions } from "../shared/presentation/hooks/usePermissions";
import { useBankTransactionPermissions } from "../shared/presentation/hooks/useBankTransactionPermissions";
import { useBankTransactionReviewData } from "../shared/presentation/hooks/useBankTransactionReviewData";
import { MODULE_CODES, ACTION_CODES } from "../shared/constants/permissions";
import { ReviewRequestModal } from "../shared/presentation/components/ReviewRequestModal";

interface CustomerPaymentTransactionModalProps {
  open: boolean;
  zoho_bankaccountid: string | null;
  transactionData: Transaction | null;
  onOpenChange: (open: boolean) => void;
  onTransactionMatched?: (transactionId: string, amount: number) => void;
  onReviewRequestSubmitted?: () => void;
  allBankAccounts?: BankAccount[];
  currentBankAccountId?: string;
  currentBankAccountName?: string;
  currentAccountType?: "bank" | "credit";
}

interface CustomerPaymentTdsLineItem {
  line_item_id: string;
  line_index: number;
  name: string;
  description: string;
  item_total: number;
  quantity: number;
  rate: number;
  tax_name?: string;
  tax_percentage?: number;
  tds_tax_amount: number;
  tds_section?: string;
  tds_tax_id?: string;
  tds_tax_percentage?: number;
  tds_tax_name?: string;
  is_tds_applied?: boolean;
}

interface CustomerPaymentSelectedInvoice {
  invoice: CustomerInvoice;
  amount: number;
  adjustmentAmount?: number;
  tds_tax_amount: number | undefined;
  tds_tax_id: string | undefined;
  tds_section: string | undefined;
  tds_tax_percentage: number | undefined;
  tds_tax_name: string | undefined;
  is_tds_applied: boolean | undefined | number;
  isSelected: boolean;
  tds_line_items?: CustomerPaymentTdsLineItem[];
}

const CustomerPaymentTransactionModal: React.FC<
  CustomerPaymentTransactionModalProps
> = ({
  open,
  onOpenChange,
  transactionData,
  zoho_bankaccountid,
  onTransactionMatched,
  onReviewRequestSubmitted,
  allBankAccounts = [],
  currentBankAccountId = "",
  currentBankAccountName = "",
  currentAccountType,
}) => {
  // Toast and error handling
  const { showSuccess, showError, showWarning } = useToast();
  const {
    errors,
    setApiErrors,
    setFieldError,
    clearErrors,
    hasErrors: checkHasErrors,
  } = useErrorHandling();

  // Permissions
  const { getPermissionInfo, hasPermission } = usePermissions();

  // Bank transaction permissions hook
  const {
    canAdvance: canAdvanceFromHook,
    canMatch,
    canSplitTransaction,
    needsReviewForAdvance,
    needsReviewForMatch,
    needsReviewForSplitTransaction,
    isInitialLoading,
  } = useBankTransactionPermissions();

  // Bank transaction review data hook
  const {
    reviewStatus,
    hasReview,
    reviewForPopulation,
    isLoading: isReviewDataLoading,
  } = useBankTransactionReviewData(transactionData);
  const isKnownUnderReview =
    transactionData?.review_status === "pending" || reviewStatus === "pending";
  const isWaitingForReviewData = isKnownUnderReview && !reviewForPopulation;

  const canSplit = hasPermission(
    MODULE_CODES.BANK_TRANSACTION,
    ACTION_CODES.SPLIT_TRANSACTION,
  );
  const advanceInfo = getPermissionInfo(
    MODULE_CODES.BANK_TRANSACTION,
    ACTION_CODES.ADVANCE,
  );
  const canAdvance = canAdvanceFromHook || advanceInfo.granted;

  // Closed month state
  const [showClosedMonthConfirmModal, setShowClosedMonthConfirmModal] =
    useState(false);

  // Review request modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingMatchAction, setPendingMatchAction] = useState<{
    action: () => void;
    changeDetails?: ChangeDetails;
    actionCode?: string;
  } | null>(null);

  // Closed month check (transaction date)
  const transactionMonth = transactionData?.date
    ? dateToMonthYear(transactionData.date)
    : undefined;
  const { isClosed: isMonthClosed } = useIsMonthClosed(transactionMonth);
  const isAdminOrCFO = useIsAdminOrCFO();

  // Helper function to generate unique IDs
  const generateId = () => {
    return `customer-payment-instance-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
  };

  // State for multiple customer payment instances
  interface CustomerPaymentInstance {
    id: string;
    customerId: string | null;
    customerName: string | null;
    selectedInvoices: CustomerPaymentSelectedInvoice[];
    advancePayment: number;
    onAccountAmount: number;
    totalInvoiceAmounts: number;
    bankExchangeRate: number | null;
  }

  const [customerPaymentInstances, setCustomerPaymentInstances] = useState<
    CustomerPaymentInstance[]
  >([
    {
      id: generateId(),
      customerId: null,
      customerName: null,
      selectedInvoices: [],
      advancePayment: 0,
      onAccountAmount: 0,
      totalInvoiceAmounts: 0,
      bankExchangeRate: null,
    },
  ]);

  // Refs for scrolling to instances
  const instanceRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const newlyAddedInstanceIdRef = useRef<string | null>(null);

  // State for TDS rates
  const [tdsRates, setTdsRates] = useState<TDSRate[]>([]);
  const [loadingTdsRates, setLoadingTdsRates] = useState(false);

  // State for matching/categorization
  const [isMatching, setIsMatching] = useState(false);
  const [knockOffOpeningBalance, setKnockOffOpeningBalance] = useState(false);

  // State for transfer form data
  const [transferFromAccountId, setTransferFromAccountId] =
    useState<string>("");
  const [transferDate, setTransferDate] = useState<string>("");
  const [transferReferenceNumber, setTransferReferenceNumber] =
    useState<string>("");
  const [transferTransactionExists, setTransferTransactionExists] =
    useState<boolean>(true);
  const [transferFromTransactionId, setTransferFromTransactionId] =
    useState<string>("");

  // State for interest income form data
  const [interestIncomeFromAccount, setInterestIncomeFromAccount] =
    useState<string>("");
  const [interestIncomeDate, setInterestIncomeDate] = useState<string>("");
  const [interestIncomeReferenceNumber, setInterestIncomeReferenceNumber] =
    useState<string>("");
  const [interestIncomePaymentMode, setInterestIncomePaymentMode] =
    useState<string>("Bank Transfer");

  // State for other income form data
  const [otherIncomeFromAccount, setOtherIncomeFromAccount] =
    useState<string>("");
  const [otherIncomeDate, setOtherIncomeDate] = useState<string>("");
  const [otherIncomePaymentMode, setOtherIncomePaymentMode] =
    useState<string>("Bank Transfer");
  const [otherIncomeReferenceNumber, setOtherIncomeReferenceNumber] =
    useState<string>("");

  // State for deposit from other accounts form data
  const [depositDate, setDepositDate] = useState<string>("");
  const [depositPaymentMode, setDepositPaymentMode] =
    useState<string>("Bank Transfer");
  const [depositFromAccount, setDepositFromAccount] = useState<string>("");
  const [depositReceivedFrom, setDepositReceivedFrom] = useState<string>("");
  const [depositReferenceNumber, setDepositReferenceNumber] =
    useState<string>("");

  // State for deposit split mode
  const [isDepositSplitMode, setIsDepositSplitMode] = useState<boolean>(false);
  const [depositSplits, setDepositSplits] = useState<SplitItem[]>([]);

  // State for owners contribution form data
  const [ownersContributionFromAccount, setOwnersContributionFromAccount] =
    useState<string>("");
  const [ownersContributionDate, setOwnersContributionDate] =
    useState<string>("");
  const [ownersContributionPaymentMode, setOwnersContributionPaymentMode] =
    useState<string>("Bank Transfer");
  const [
    ownersContributionReferenceNumber,
    setOwnersContributionReferenceNumber,
  ] = useState<string>("");

  // State for vendor payment refund form data
  const [vendorRefundVendor, setVendorRefundVendor] = useState<{
    vendor_id: string;
    vendor_name: string;
  }>({ vendor_id: "", vendor_name: "" });
  const [vendorRefundDate, setVendorRefundDate] = useState<string>("");
  const [vendorRefundReferenceNumber, setVendorRefundReferenceNumber] =
    useState<string>("");
  const [vendorRefundPaymentMode, setVendorRefundPaymentMode] =
    useState<string>("Bank Transfer");
  const [vendorRefundSelectedPayment, setVendorRefundSelectedPayment] =
    useState<{
      payment_id: string;
      payment_number: string;
      amount: number;
    } | null>(null);

  // Credits or Refund Received (Money IN on credit card only)
  const [creditsOrRefundDate, setCreditsOrRefundDate] = useState<string>("");
  const [creditsOrRefundFromAccountId, setCreditsOrRefundFromAccountId] =
    useState<string>("");
  const [creditsOrRefundPaymentMode, setCreditsOrRefundPaymentMode] =
    useState<string>("Bank Transfer");
  const [creditsOrRefundReferenceNumber, setCreditsOrRefundReferenceNumber] =
    useState<string>("");
  const [creditsOrRefundVendor, setCreditsOrRefundVendor] = useState<{
    vendor_id: string;
    vendor_name: string;
  } | null>(null);

  // State for activity logs
  const [activityLogs, setActivityLogs] = useState<TransactionActivityLog[]>(
    [],
  );
  const [isLoadingActivityLogs, setIsLoadingActivityLogs] = useState(false);

  // State for expense GL selection (customer expense refund; dropdown uses fetchExpenseGlsPaginated)
  const [selectedExpenseGlAccountId, setSelectedExpenseGlAccountId] =
    useState<string>("");
  const [selectedExpenseGlName, setSelectedExpenseGlName] =
    useState<string>("");

  // State for form data
  const [selectedCategory, setSelectedCategory] =
    useState<string>("Customer Payment");
  const [formChanges, setFormChanges] = useState<{
    [key: string]: any;
  }>({});

  const transactionId = transactionData?.transaction_id || "";
  const categoryPrefilledRef = useRef(false);
  const reviewPopulatedRef = useRef(false); // Track if we've populated from review

  // Suggestion UX: track when values are from API recommendation; badge shows until user changes
  const [categoryFromSuggestion, setCategoryFromSuggestion] = useState(false);
  const [categorySuggestionApplied, setCategorySuggestionApplied] =
    useState(false);
  const [initialPartyLoaded, setInitialPartyLoaded] = useState(false);
  const hasSuggestedCategory = !!(
    transactionData?.zoho_category || transactionData?.category_type
  );
  const hasSuggestedParty = !!(
    transactionData?.zoho_partyid &&
    transactionData?.zoho_partyid !== "create_new"
  );
  const suggestedCategoryValue =
    transactionData?.zoho_category || transactionData?.category_type;
  const suggestionApplying =
    (hasSuggestedCategory || hasSuggestedParty) &&
    ((hasSuggestedCategory && !categorySuggestionApplied) ||
      (selectedCategory === "Customer Payment" &&
        hasSuggestedParty &&
        !initialPartyLoaded));
  // Store raw invoice data from review for matching after invoices are loaded
  const reviewRawInvoicesRef = useRef<
    Map<
      string,
      Array<{ invoice_id: string; amount_applied: number; [key: string]: any }>
    >
  >(new Map());
  // Track which instances have had invoices matched to prevent re-matching
  const invoicesMatchedRef = useRef<Set<string>>(new Set());
  // Loading state when populating from review
  const [isPopulatingFromReview, setIsPopulatingFromReview] = useState(false);

  const loadActivityLogs = useCallback(async () => {
    if (!transactionId) return;
    try {
      setIsLoadingActivityLogs(true);
      const response = await fetchTransactionActivityLogs(transactionId);
      const logs = Array.isArray(response.data?.logs)
        ? response.data.logs
        : Array.isArray(response.data)
          ? response.data
          : [];
      setActivityLogs(logs);
    } catch (error) {
      console.error("Failed to load activity logs:", error);
    } finally {
      setIsLoadingActivityLogs(false);
    }
  }, [transactionId]);

  // Fetch tds rates and activity logs when modal opens
  useEffect(() => {
    if (open) {
      fetchTDSRatesData();
      loadActivityLogs();
      categoryPrefilledRef.current = false;
      reviewPopulatedRef.current = false; // Reset on open
      reviewRawInvoicesRef.current.clear(); // Clear raw invoices data
      invoicesMatchedRef.current.clear(); // Clear matched instances
      setCategorySuggestionApplied(false);
      setCategoryFromSuggestion(false);
      setInitialPartyLoaded(false);
    } else {
      setActivityLogs([]);
      categoryPrefilledRef.current = false;
      reviewPopulatedRef.current = false; // Reset on close
      reviewRawInvoicesRef.current.clear(); // Clear raw invoices data
      invoicesMatchedRef.current.clear(); // Clear matched instances
      setCategorySuggestionApplied(false);
      setCategoryFromSuggestion(false);
      setInitialPartyLoaded(false);
    }
  }, [open, loadActivityLogs]);

  // Auto-select category based on zoho_category (priority) or backend category_type (fallback)
  useEffect(() => {
    if (!open || categoryPrefilledRef.current) return;
    const isTallyBackend = getStoredAccountingBackend() === "tally";
    const categoryTypeMap: Record<string, string> = {
      customer_payment: "Customer Payment",
      transfer_from_another_account: "Transfer From Another Account",
      interest_income: "Interest Income",
      other_income: "Other Income",
      deposit_from_other_accounts: "Deposit From Other Accounts",
      ...(isTallyBackend ? {} : { owners_contribution: "Owners Contribution" }),
      customer_expense_refund: "Customer Expense Refund",
      vendor_payment_refund: "Vendor Payment Refund",
      credits_or_refund_received: "Credits or Refunds Received",
    };

    // Priority: Use zoho_category if available, otherwise fallback to category_type
    const categoryValue =
      transactionData?.zoho_category || transactionData?.category_type;
    const mappedCategory = categoryValue && categoryTypeMap[categoryValue];

    if (mappedCategory) {
      setSelectedCategory(mappedCategory);
      categoryPrefilledRef.current = true;
      setCategorySuggestionApplied(true);
      setCategoryFromSuggestion(true);
    } else if (categoryValue) {
      // API sent a category we don't map - stop waiting, unblock UI
      setCategorySuggestionApplied(true);
    }
  }, [open, transactionData?.zoho_category, transactionData?.category_type]);

  // Initialize first instance with auto-select customer if provided
  useEffect(() => {
    if (
      open &&
      selectedCategory === "Customer Payment" &&
      transactionData?.zoho_partyid &&
      transactionData?.zoho_partyid !== "create_new" &&
      customerPaymentInstances.length === 1 &&
      !customerPaymentInstances[0].customerId
    ) {
      // Auto-select will be handled by CustomerPaymentReco component
      // Just pass the auto-select props
    }
  }, [
    open,
    selectedCategory,
    transactionData?.zoho_partyid,
    customerPaymentInstances,
  ]);

  // Auto-scroll to newly added instance
  useEffect(() => {
    if (newlyAddedInstanceIdRef.current) {
      const instanceId = newlyAddedInstanceIdRef.current;
      const instanceRef = instanceRefs.current.get(instanceId);
      if (instanceRef) {
        setTimeout(() => {
          instanceRef.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 100);
      }
      newlyAddedInstanceIdRef.current = null;
    }
  }, [customerPaymentInstances]);

  // Set category from review raw_json when review exists (view-only mode)
  useEffect(() => {
    if (
      !open ||
      !reviewForPopulation ||
      reviewPopulatedRef.current ||
      reviewStatus === "rejected"
    ) {
      setIsPopulatingFromReview(false);
      return;
    }

    // Only set category if review is pending or approved (for view-only mode)
    if (reviewStatus !== "pending") {
      setIsPopulatingFromReview(false);
      return;
    }

    const rawJson = reviewForPopulation.change_details?.raw_json;
    if (!rawJson) {
      setIsPopulatingFromReview(false);
      return;
    }

    setIsPopulatingFromReview(false); // No loading needed for view-only mode

    // Map category_type to selectedCategory
    const categoryTypeMap: Record<string, string> = {
      customer_payment: "Customer Payment",
      split_customer: "Customer Payment",
      transfer_from_another_account: "Transfer From Another Account",
      interest_income: "Interest Income",
      other_income: "Other Income",
      deposit_from_other_accounts: "Deposit From Other Accounts",
      deposit_split: "Deposit From Other Accounts",
      owners_contribution: "Owners Contribution",
      customer_expense_refund: "Customer Expense Refund",
      vendor_payment_refund: "Vendor Payment Refund",
      credits_or_refund_received: "Credits or Refunds Received",
    };

    const categoryType = rawJson.category_type;
    if (categoryType && categoryTypeMap[categoryType]) {
      setSelectedCategory(categoryTypeMap[categoryType]);
      categoryPrefilledRef.current = true;
    }

    reviewPopulatedRef.current = true;
  }, [open, reviewForPopulation, reviewStatus]);

  // Calculate total allocated amounts from other instances (excluding specified instance)
  const calculateTotalAllocatedAmountsFromOtherInstances = useCallback(
    (excludeInstanceId: string): number => {
      return customerPaymentInstances
        .filter((instance) => instance.id !== excludeInstanceId)
        .reduce((sum, instance) => sum + instance.totalInvoiceAmounts, 0);
    },
    [customerPaymentInstances],
  );

  // Calculate total advance payments across all instances
  const calculateTotalAdvancePayments = useCallback((): number => {
    return customerPaymentInstances.reduce(
      (sum, instance) => sum + instance.advancePayment,
      0,
    );
  }, [customerPaymentInstances]);

  const calculateTotalOnAccountAmounts = useCallback((): number => {
    return customerPaymentInstances.reduce(
      (sum, instance) => sum + instance.onAccountAmount,
      0,
    );
  }, [customerPaymentInstances]);

  // Calculate total invoice amounts across all instances
  const calculateTotalInvoiceAmounts = useCallback((): number => {
    return customerPaymentInstances.reduce(
      (sum, instance) => sum + instance.totalInvoiceAmounts,
      0,
    );
  }, [customerPaymentInstances]);

  // Validate match requirements for multiple instances
  const validateMatchRequirements = useCallback((): boolean => {
    if (customerPaymentInstances.length === 1) {
      return true; // No validation needed for single instance
    }
    const totalAmounts = calculateTotalInvoiceAmounts();
    const totalAdvance = calculateTotalAdvancePayments();
    const totalOnAccount = calculateTotalOnAccountAmounts();
    const total = totalAmounts + totalAdvance + totalOnAccount;
    const transactionAmountValue = transactionData?.amount || 0;

    // Round both values to 2 decimal places to normalize floating-point precision errors
    // Then compare exactly for strict cent-level matching
    // This is the standard approach for currency validation in production
    const roundedTotal = Math.round(total * 100) / 100;
    const roundedTransactionAmount =
      Math.round(transactionAmountValue * 100) / 100;

    return roundedTotal === roundedTransactionAmount;
  }, [
    customerPaymentInstances,
    calculateTotalInvoiceAmounts,
    calculateTotalAdvancePayments,
    calculateTotalOnAccountAmounts,
    transactionData?.amount,
  ]);

  // Handle adding a new customer instance
  const handleAddCustomer = useCallback(() => {
    // Check split_transaction permission
    if (!canSplit) {
      showError("You do not have permission to split transactions");
      return;
    }

    const newInstance: CustomerPaymentInstance = {
      id: generateId(),
      customerId: null,
      customerName: null,
      selectedInvoices: [],
      advancePayment: 0,
      onAccountAmount: 0,
      totalInvoiceAmounts: 0,
      bankExchangeRate: null,
    };
    setCustomerPaymentInstances((prev) => {
      const newInstances = [...prev, newInstance];
      // If transitioning from 1 to multiple instances, clear advance payments from all instances
      if (prev.length === 1) {
        return newInstances.map((instance) => ({
          ...instance,
          advancePayment: 0,
          onAccountAmount: 0,
        }));
      }
      return newInstances;
    });
    newlyAddedInstanceIdRef.current = newInstance.id;
  }, [canSplit, showError]);

  // Handle removing a customer instance
  const handleRemoveCustomer = useCallback(
    (instanceId: string) => {
      if (customerPaymentInstances.length <= 1) {
        return; // Don't allow removing the last instance
      }
      setCustomerPaymentInstances((prev) =>
        prev.filter((instance) => instance.id !== instanceId),
      );
      instanceRefs.current.delete(instanceId);
      instanceChangeCallbacksRef.current.delete(instanceId);
    },
    [customerPaymentInstances.length],
  );

  // Handle instance change from CustomerPaymentReco
  const handleInstanceChange = useCallback(
    (
      instanceId: string,
      data: {
        customerId: string | null;
        customerName: string | null;
        selectedInvoices: CustomerPaymentSelectedInvoice[];
        advancePayment: number;
        onAccountAmount: number;
        totalInvoiceAmounts: number;
        bankExchangeRate: number | null;
      },
    ) => {
      // Normal flow - update instance data
      setCustomerPaymentInstances((prev) =>
        prev.map((instance) =>
          instance.id === instanceId
            ? {
                ...instance,
                customerId: data.customerId,
                customerName: data.customerName,
                selectedInvoices: data.selectedInvoices,
                advancePayment: data.advancePayment,
                onAccountAmount: data.onAccountAmount,
                totalInvoiceAmounts: data.totalInvoiceAmounts,
                bankExchangeRate: data.bankExchangeRate,
              }
            : instance,
        ),
      );
    },
    [],
  );

  // Ref callback for instance refs
  const setInstanceRef = useCallback((instanceId: string) => {
    return (el: HTMLDivElement | null) => {
      if (el) {
        instanceRefs.current.set(instanceId, el);
      } else {
        instanceRefs.current.delete(instanceId);
      }
    };
  }, []);

  // Memoized onChange callbacks per instance to avoid infinite loops
  const instanceChangeCallbacksRef = useRef<
    Map<
      string,
      (data: {
        customerId: string | null;
        customerName: string | null;
        selectedInvoices: CustomerPaymentSelectedInvoice[];
        advancePayment: number;
        onAccountAmount: number;
        totalInvoiceAmounts: number;
        bankExchangeRate: number | null;
      }) => void
    >
  >(new Map());

  // Store handleInstanceChange in a ref to avoid recreating callbacks
  const handleInstanceChangeRef = useRef(handleInstanceChange);
  useEffect(() => {
    handleInstanceChangeRef.current = handleInstanceChange;
  }, [handleInstanceChange]);

  const getInstanceChangeCallback = useCallback((instanceId: string) => {
    if (!instanceChangeCallbacksRef.current.has(instanceId)) {
      instanceChangeCallbacksRef.current.set(instanceId, (data) => {
        handleInstanceChangeRef.current(instanceId, data);
      });
    }
    return instanceChangeCallbacksRef.current.get(instanceId)!;
  }, []);

  const fetchTDSRatesData = async () => {
    setLoadingTdsRates(true);
    try {
      const response = await fetchTDSRates();
      if (response.success) {
        setTdsRates(response.data.tds_rates);
      }
    } catch (error) {
      console.error("Failed to fetch TDS rates:", error);
    } finally {
      setLoadingTdsRates(false);
    }
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };

  // Helper function to format numbers with 2 decimal places (Indian format)
  const formatAmount = (amount: number): string => {
    return amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Helper function to extract meaningful error message
  const extractErrorMessage = (error: any): string => {
    if (!error) return "An unknown error occurred";

    console.log("Full error object:", error);

    // Check for validation errors with errors object
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors as Record<string, unknown>;
      const errorMessages = Object.keys(errors)
        .map((field) => `${field}: ${errors[field]}`)
        .join("\n");

      const validationMessage =
        error.response?.data?.message || "Validation failed";
      return `${validationMessage}\n\n${errorMessages}`;
    }

    let errorMessage = "";

    // Extract error message from various error object structures
    // Check for Axios error response structure first
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    } else if (error.error) {
      errorMessage = error.error;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === "string") {
      errorMessage = error;
    } else {
      errorMessage = "An unknown error occurred";
    }

    console.log("Original error from API:", errorMessage);

    // Check for Zoho API errors
    if (errorMessage.includes("Zoho API request failed:")) {
      const zohoErrorMatch = errorMessage.match(
        /Zoho API request failed:\s*(.+)/,
      );
      if (zohoErrorMatch && zohoErrorMatch[1]) {
        const cleanMessage = zohoErrorMatch[1].trim();
        console.log("Extracted Zoho error:", cleanMessage);
        return cleanMessage;
      }
    }

    // For simple messages, check if there's nested "Error: " pattern
    const lastErrorIndex = errorMessage.lastIndexOf("Error: ");
    if (lastErrorIndex !== -1) {
      const extractedMessage = errorMessage
        .substring(lastErrorIndex + 7) // "Error: " is 7 characters
        .trim();
      if (extractedMessage) {
        console.log("Extracted clean message:", extractedMessage);
        return extractedMessage;
      }
    }

    // Return the message as is
    console.log("Returning message as is:", errorMessage);
    return errorMessage;
  };

  const handleClose = () => {
    // Reset form state when closing
    setSelectedCategory("Customer Payment");
    setCustomerPaymentInstances([
      {
        id: generateId(),
        customerId: null,
        customerName: null,
        selectedInvoices: [],
        advancePayment: 0,
        onAccountAmount: 0,
        totalInvoiceAmounts: 0,
        bankExchangeRate: null,
      },
    ]);
    setFormChanges({});
    setKnockOffOpeningBalance(false);
    instanceRefs.current.clear();
    newlyAddedInstanceIdRef.current = null;
    // Reset transfer form
    setTransferFromAccountId("");
    setTransferDate("");
    setTransferReferenceNumber("");
    setTransferTransactionExists(false);
    setTransferFromTransactionId("");
    // Reset interest income form
    setInterestIncomeFromAccount("");
    setInterestIncomeDate("");
    setInterestIncomeReferenceNumber("");
    setInterestIncomePaymentMode("Bank Transfer");
    // Reset other income form
    setOtherIncomeFromAccount("");
    setOtherIncomeDate("");
    setOtherIncomePaymentMode("Bank Transfer");
    setOtherIncomeReferenceNumber("");
    // Reset deposit from other accounts form
    setDepositDate("");
    setDepositPaymentMode("Bank Transfer");
    setDepositFromAccount("");
    setDepositReceivedFrom("");
    setDepositReferenceNumber("");
    // Reset owners contribution form
    setOwnersContributionFromAccount("");
    setOwnersContributionDate("");
    setOwnersContributionPaymentMode("Bank Transfer");
    setOwnersContributionReferenceNumber("");
    // Reset vendor payment refund form
    setVendorRefundVendor({ vendor_id: "", vendor_name: "" });
    setVendorRefundDate("");
    setVendorRefundReferenceNumber("");
    setVendorRefundPaymentMode("Bank Transfer");
    setVendorRefundSelectedPayment(null);
    setSelectedExpenseGlAccountId("");
    setSelectedExpenseGlName("");
    clearErrors();
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset form state when closing by any means (including clicking outside)
      setSelectedCategory("Customer Payment");
      setCustomerPaymentInstances([
        {
          id: generateId(),
          customerId: null,
          customerName: null,
          selectedInvoices: [],
          advancePayment: 0,
          onAccountAmount: 0,
          totalInvoiceAmounts: 0,
          bankExchangeRate: null,
        },
      ]);
      setFormChanges({});
      setKnockOffOpeningBalance(false);
      instanceRefs.current.clear();
      newlyAddedInstanceIdRef.current = null;
      // Reset transfer form
      setTransferFromAccountId("");
      setTransferDate("");
      setTransferReferenceNumber("");
      setTransferTransactionExists(false);
      setTransferFromTransactionId("");
      // Reset interest income form
      setInterestIncomeFromAccount("");
      setInterestIncomeDate("");
      setInterestIncomeReferenceNumber("");
      setInterestIncomePaymentMode("Bank Transfer");
      // Reset other income form
      setOtherIncomeFromAccount("");
      setOtherIncomeDate("");
      setOtherIncomePaymentMode("Bank Transfer");
      setOtherIncomeReferenceNumber("");
      // Reset deposit from other accounts form
      setDepositDate("");
      setDepositPaymentMode("Bank Transfer");
      setDepositFromAccount("");
      setDepositReceivedFrom("");
      setDepositReferenceNumber("");
      // Reset owners contribution form
      setOwnersContributionFromAccount("");
      setOwnersContributionDate("");
      setOwnersContributionPaymentMode("Bank Transfer");
      setOwnersContributionReferenceNumber("");
      // Reset vendor payment refund form
      setVendorRefundVendor({ vendor_id: "", vendor_name: "" });
      setVendorRefundDate("");
      setVendorRefundReferenceNumber("");
      setVendorRefundPaymentMode("Bank Transfer");
      setVendorRefundSelectedPayment(null);
      setSelectedExpenseGlAccountId("");
      setSelectedExpenseGlName("");
      // Clear errors
      clearErrors();
    }
    onOpenChange(newOpen);
  };

  // Helper function to build field changes for a category
  const buildFieldChangesForCategory = (
    category: string,
    oldData: any,
    newData: any,
  ): Array<{ field: string; old: string; new: string }> => {
    const changes: Array<{ field: string; old: string; new: string }> = [];

    switch (category) {
      case "Customer Payment":
        // Advance payment changes
        if (oldData?.advance_payment !== newData?.advance_payment) {
          changes.push({
            field: "Advance Payment",
            old: (oldData?.advance_payment || 0).toString(),
            new: (newData?.advance_payment || 0).toString(),
          });
        }

        // Invoice count changes
        const oldInvoiceCount =
          oldData?.categorize_in_zoho_json?.invoices?.length || 0;
        const newInvoiceCount =
          newData?.categorize_in_zoho_json?.invoices?.length || 0;
        if (oldInvoiceCount !== newInvoiceCount) {
          changes.push({
            field: "Number of Invoices",
            old: oldInvoiceCount.toString(),
            new: newInvoiceCount.toString(),
          });
        }

        // TDS changes per invoice
        newData?.categorize_in_zoho_json?.invoices?.forEach(
          (invoice: any, index: number) => {
            const oldInvoice =
              oldData?.categorize_in_zoho_json?.invoices?.[index];
            if (oldInvoice) {
              if (oldInvoice.adjustment_amount !== invoice.adjustment_amount) {
                changes.push({
                  field: `Invoice ${invoice.invoice_number}: Adjustment`,
                  old: (oldInvoice.adjustment_amount || 0).toString(),
                  new: (invoice.adjustment_amount || 0).toString(),
                });
              }
              if (oldInvoice.tds_tax_amount !== invoice.tds_tax_amount) {
                changes.push({
                  field: `Invoice ${invoice.invoice_number}: TDS Amount`,
                  old: (oldInvoice.tds_tax_amount || 0).toString(),
                  new: (invoice.tds_tax_amount || 0).toString(),
                });
              }
            }
          },
        );

        // Exchange rate changes (for foreign currency)
        if (
          oldData?.categorize_in_zoho_json?.exchange_rate !==
          newData?.categorize_in_zoho_json?.exchange_rate
        ) {
          changes.push({
            field: "Exchange Rate",
            old: (
              oldData?.categorize_in_zoho_json?.exchange_rate || "N/A"
            ).toString(),
            new: (
              newData?.categorize_in_zoho_json?.exchange_rate || "N/A"
            ).toString(),
          });
        }

        // Bank charges changes
        if (
          oldData?.categorize_in_zoho_json?.bank_charges !==
          newData?.categorize_in_zoho_json?.bank_charges
        ) {
          changes.push({
            field: "Bank Charges",
            old: (
              oldData?.categorize_in_zoho_json?.bank_charges || 0
            ).toString(),
            new: (
              newData?.categorize_in_zoho_json?.bank_charges || 0
            ).toString(),
          });
        }
        break;

      case "Transfer From Another Account":
        if (oldData?.from_account_id !== newData?.from_account_id) {
          changes.push({
            field: "From Account",
            old: oldData?.from_account_id || "N/A",
            new: newData?.from_account_id || "N/A",
          });
        }
        if (oldData?.date !== newData?.date) {
          changes.push({
            field: "Transfer Date",
            old: oldData?.date || "N/A",
            new: newData?.date || "N/A",
          });
        }
        if (oldData?.reference_number !== newData?.reference_number) {
          changes.push({
            field: "Reference Number",
            old: oldData?.reference_number || "N/A",
            new: newData?.reference_number || "N/A",
          });
        }
        break;

      case "Interest Income":
        if (oldData?.from_account_id !== newData?.from_account_id) {
          changes.push({
            field: "From Account",
            old: oldData?.from_account_id || "N/A",
            new: newData?.from_account_id || "N/A",
          });
        }
        if (oldData?.date !== newData?.date) {
          changes.push({
            field: "Date",
            old: oldData?.date || "N/A",
            new: newData?.date || "N/A",
          });
        }
        if (oldData?.payment_mode !== newData?.payment_mode) {
          changes.push({
            field: "Payment Mode",
            old: oldData?.payment_mode || "N/A",
            new: newData?.payment_mode || "N/A",
          });
        }
        if (oldData?.reference_number !== newData?.reference_number) {
          changes.push({
            field: "Reference Number",
            old: oldData?.reference_number || "N/A",
            new: newData?.reference_number || "N/A",
          });
        }
        break;

      case "Other Income":
        if (oldData?.from_account_id !== newData?.from_account_id) {
          changes.push({
            field: "From Account",
            old: oldData?.from_account_id || "N/A",
            new: newData?.from_account_id || "N/A",
          });
        }
        if (oldData?.date !== newData?.date) {
          changes.push({
            field: "Date",
            old: oldData?.date || "N/A",
            new: newData?.date || "N/A",
          });
        }
        if (oldData?.payment_mode !== newData?.payment_mode) {
          changes.push({
            field: "Payment Mode",
            old: oldData?.payment_mode || "N/A",
            new: newData?.payment_mode || "N/A",
          });
        }
        break;

      case "Deposit From Other Accounts":
        if (oldData?.from_account_id !== newData?.from_account_id) {
          changes.push({
            field: "From Account",
            old: oldData?.from_account_id || "N/A",
            new: newData?.from_account_id || "N/A",
          });
        }
        if (oldData?.date !== newData?.date) {
          changes.push({
            field: "Date",
            old: oldData?.date || "N/A",
            new: newData?.date || "N/A",
          });
        }
        if (oldData?.customer_id !== newData?.customer_id) {
          changes.push({
            field: "Customer",
            old: oldData?.customer_id || "N/A",
            new: newData?.customer_id || "N/A",
          });
        }
        break;

      case "Owners Contribution":
        if (oldData?.from_account_id !== newData?.from_account_id) {
          changes.push({
            field: "From Account",
            old: oldData?.from_account_id || "N/A",
            new: newData?.from_account_id || "N/A",
          });
        }
        if (oldData?.date !== newData?.date) {
          changes.push({
            field: "Date",
            old: oldData?.date || "N/A",
            new: newData?.date || "N/A",
          });
        }
        if (oldData?.payment_mode !== newData?.payment_mode) {
          changes.push({
            field: "Payment Mode",
            old: oldData?.payment_mode || "N/A",
            new: newData?.payment_mode || "N/A",
          });
        }
        break;

      case "Vendor Payment Refund":
        if (oldData?.vendor_id !== newData?.vendor_id) {
          changes.push({
            field: "Vendor",
            old: oldData?.vendor_name || "N/A",
            new: newData?.vendor_name || "N/A",
          });
        }
        if (oldData?.payment?.payment_id !== newData?.payment?.payment_id) {
          changes.push({
            field: "Payment",
            old: oldData?.payment?.payment_number || "N/A",
            new: newData?.payment?.payment_number || "N/A",
          });
        }
        if (oldData?.date !== newData?.date) {
          changes.push({
            field: "Date",
            old: oldData?.date || "N/A",
            new: newData?.date || "N/A",
          });
        }
        break;

      case "Credits or Refunds Received":
        if (oldData?.from_account_id !== newData?.from_account_id) {
          changes.push({
            field: "From Account",
            old: oldData?.from_account_id || "N/A",
            new: newData?.from_account_id || "N/A",
          });
        }
        if (oldData?.date !== newData?.date) {
          changes.push({
            field: "Date",
            old: oldData?.date || "N/A",
            new: newData?.date || "N/A",
          });
        }
        if (oldData?.payment_mode !== newData?.payment_mode) {
          changes.push({
            field: "Received Via",
            old: oldData?.payment_mode || "N/A",
            new: newData?.payment_mode || "N/A",
          });
        }
        if (oldData?.vendor_id !== newData?.vendor_id) {
          changes.push({
            field: "Received From",
            old: oldData?.vendor_name || "N/A",
            new: newData?.vendor_name || "N/A",
          });
        }
        break;

      case "Customer Expense Refund":
        if (oldData?.from_account_id !== newData?.from_account_id) {
          changes.push({
            field: "Expense GL Account",
            old: oldData?.from_account_id || "N/A",
            new: newData?.from_account_id || "N/A",
          });
        }
        if (oldData?.date !== newData?.date) {
          changes.push({
            field: "Date",
            old: oldData?.date || "N/A",
            new: newData?.date || "N/A",
          });
        }
        break;
    }

    return changes;
  };

  // Helper function to build request body for a category
  // Helper function to build empty old data for comparison
  const buildEmptyOldDataForCategory = (category: string): any => {
    switch (category) {
      case "Customer Payment":
        return {
          advance_payment: 0,
          categorize_in_zoho_json: {
            invoices: [],
            exchange_rate: null,
            bank_charges: null,
          },
        };
      case "Transfer From Another Account":
        return {
          from_account_id: "",
          date: "",
          reference_number: "",
        };
      case "Interest Income":
        return {
          from_account_id: "",
          date: "",
          payment_mode: "",
          reference_number: "",
        };
      case "Other Income":
        return {
          from_account_id: "",
          date: "",
          payment_mode: "",
          reference_number: "",
        };
      case "Deposit From Other Accounts":
        return {
          from_account_id: "",
          date: "",
          payment_mode: "",
          reference_number: "",
          customer_id: "",
        };
      case "Owners Contribution":
        return {
          from_account_id: "",
          date: "",
          payment_mode: "",
          reference_number: "",
        };
      case "Vendor Payment Refund":
        return {
          vendor_id: "",
          vendor_name: "",
          date: "",
          payment_mode: "",
          reference_number: "",
          payment: null,
        };
      case "Credits or Refunds Received":
        return {
          from_account_id: "",
          date: "",
          payment_mode: "",
          reference_number: "",
          vendor_id: null,
          vendor_name: null,
        };
      case "Customer Expense Refund":
        return {
          from_account_id: "",
          date: "",
        };
      default:
        return {};
    }
  };

  const getPersistedTdsLineItems = (item: CustomerPaymentSelectedInvoice) =>
    (item.tds_line_items || []).map((line) => ({
      line_item_id: line.line_item_id,
      line_index: line.line_index,
      name: line.name,
      description: line.description,
      item_total: line.item_total,
      quantity: line.quantity,
      rate: line.rate,
      tax_name: line.tax_name,
      tax_percentage: line.tax_percentage,
      tds_tax_amount: line.tds_tax_amount,
      tds_section: line.tds_section,
      tds_tax_id: line.tds_tax_id,
      tds_tax_percentage: line.tds_tax_percentage,
      tds_tax_name: line.tds_tax_name,
      is_tds_applied: line.is_tds_applied,
    }));

  const buildRequestBodyForCategory = (category: string): any => {
    if (!transactionData || !zoho_bankaccountid) {
      return {};
    }

    switch (category) {
      case "Customer Payment":
        if (customerPaymentInstances.length === 1) {
          // Single customer payment
          const instance = customerPaymentInstances[0];
          const selectedInvoicesWithAmounts = instance.selectedInvoices.filter(
            (item) => item.isSelected && item.amount > 0,
          );

          // Check for foreign currency
          const hasForeign = instance.selectedInvoices.some(
            (item) =>
              item.invoice.currency_code &&
              item.invoice.currency_code !== "INR",
          );

          const isForeign =
            hasForeign &&
            instance.bankExchangeRate &&
            instance.bankExchangeRate > 0;

          const appliedAmountFC = selectedInvoicesWithAmounts.reduce(
            (sum, item) => sum + item.amount,
            0,
          );
          const foreignForex = isForeign
            ? calculateSingleForeignPaymentForex(
                appliedAmountFC,
                instance.bankExchangeRate!,
                transactionData.amount,
              )
            : null;
          const amountForRequest = foreignForex
            ? foreignForex.amountForZoho
            : roundTo2Decimals(transactionData.amount);

          const requestBody: any = {
            category_type: "customer_payment",
            customer_name: instance.customerName,

            transaction_id: transactionData.transaction_id || "",
            advance_payment: instance.advancePayment,
            on_account_amount: instance.onAccountAmount,
            categorize_in_zoho_json: {
              customer_id: instance.customerId,
              date: transactionData.date,
              invoices: selectedInvoicesWithAmounts.map((item) => {
                if (item.invoice.is_tds_applied === 1) {
                  return {
                    id: item.invoice.id,
                    invoice_id: item.invoice.invoice_id,
                    amount_applied: roundTo2Decimals(item.amount),
                    adjustment_amount: item.adjustmentAmount || 0,
                    invoice_number: item.invoice.invoice_number,
                    total: item.invoice.total,
                    currency_symbol: item.invoice.currency_symbol,
                    balance: item.invoice.balance,
                    status: item.invoice.status,
                    is_tds_applicable: 1,
                    tds_tax_amount: "",
                    tds_section: "",
                    tds_tax_id: "",
                    tds_tax_percentage: "",
                    tds_tax_name: "",
                  };
                }
                return {
                  id: item.invoice.id,
                  invoice_id: item.invoice.invoice_id,
                  amount_applied: roundTo2Decimals(item.amount),
                  adjustment_amount: item.adjustmentAmount || 0,
                  invoice_number: item.invoice.invoice_number,
                  total: item.invoice.total,
                  currency_symbol: item.invoice.currency_symbol,
                  balance: item.invoice.balance,
                  status: item.invoice.status,
                  tds_tax_amount: item.tds_tax_amount,
                  tds_section: item.tds_section,
                  is_tds_applied: item.invoice.is_tds_applied,
                  tds_tax_id: item.tds_tax_id,
                  tds_tax_percentage: item.tds_tax_percentage,
                  tds_tax_name: item.tds_tax_name,
                  tds_line_items: getPersistedTdsLineItems(item),
                };
              }),
              amount: amountForRequest,
              account_id: zoho_bankaccountid,
            },
          };

          if (isForeign && foreignForex) {
            requestBody.categorize_in_zoho_json.exchange_rate =
              foreignForex.exchangeRateForZoho;
            requestBody.categorize_in_zoho_json.bank_charges =
              foreignForex.bankChargesFC;
          }

          return requestBody;
        } else {
          // Multiple customers
          const customers = customerPaymentInstances.map((instance) => {
            const selectedInvoicesWithAmounts =
              instance.selectedInvoices.filter(
                (item) => item.isSelected && item.amount > 0,
              );

            // Check if this instance has foreign currency invoices
            const hasForeign = instance.selectedInvoices.some(
              (item) =>
                item.isSelected &&
                item.invoice.currency_code &&
                item.invoice.currency_code !== "INR",
            );

            // Calculate bank charges for foreign currency transactions
            let exchangeRate: number | null = null;
            let bankCharges: number | null = null;

            if (
              hasForeign &&
              instance.bankExchangeRate &&
              instance.bankExchangeRate > 0
            ) {
              exchangeRate =
                Math.trunc(instance.bankExchangeRate * 1000000) / 1000000;

              // Calculate bank charges for this customer
              const realisationINR = selectedInvoicesWithAmounts.reduce(
                (sum, item) => sum + item.amount * instance.bankExchangeRate!,
                0,
              );

              const customerAllocatedINR = selectedInvoicesWithAmounts.reduce(
                (sum, item) => {
                  const invoiceAmountINR =
                    item.amount * instance.bankExchangeRate!;
                  const tdsAmount =
                    item.invoice.is_tds_applied === 1
                      ? 0
                      : (item.tds_tax_amount || 0) * instance.bankExchangeRate!;
                  return sum + invoiceAmountINR + tdsAmount;
                },
                instance.advancePayment + instance.onAccountAmount,
              );

              const calculatedBankCharges = Math.abs(
                realisationINR - customerAllocatedINR,
              );

              if (calculatedBankCharges > 0.01) {
                bankCharges =
                  Math.trunc(
                    (calculatedBankCharges / instance.bankExchangeRate!) * 100,
                  ) / 100;
              }
            }

            return {
              customer_id: instance.customerId!,
              customer_name: instance.customerName!,
              advance_payment: instance.advancePayment,
              on_account_amount: instance.onAccountAmount,
              exchange_rate: exchangeRate,
              bank_charges: bankCharges,
              invoices: selectedInvoicesWithAmounts.map((item) => {
                const invoiceData: any = {
                  id: item.invoice.id,
                  invoice_id: item.invoice.invoice_id,
                  invoice_number: item.invoice.invoice_number,
                  amount_applied: roundTo2Decimals(item.amount),
                  adjustment_amount: item.adjustmentAmount || 0,
                  total: item.invoice.total,
                  balance: item.invoice.balance,
                  currency_symbol: item.invoice.currency_symbol,
                  status: item.invoice.status,
                };

                if (item.invoice.currency_code) {
                  invoiceData.currency_code = item.invoice.currency_code;
                }

                if (item.invoice.is_tds_applied === 1) {
                  invoiceData.is_tds_applied = 1;
                  invoiceData.tds_tax_amount = "";
                  invoiceData.tds_section = "";
                  invoiceData.tds_tax_id = "";
                  invoiceData.tds_tax_percentage = "";
                  invoiceData.tds_tax_name = "";
                } else {
                  invoiceData.is_tds_applied = item.invoice.is_tds_applied || 0;
                  if (item.tds_tax_amount && item.tds_tax_amount > 0) {
                    invoiceData.tds_tax_amount = item.tds_tax_amount;
                    invoiceData.tds_section = item.tds_section || "";
                    invoiceData.tds_tax_id = item.tds_tax_id;
                    invoiceData.tds_tax_percentage = item.tds_tax_percentage;
                    invoiceData.tds_tax_name = item.tds_tax_name;
                  }
                }

                const persistedTdsLineItems = getPersistedTdsLineItems(item);
                if (persistedTdsLineItems.length > 0) {
                  invoiceData.tds_line_items = persistedTdsLineItems;
                }

                return invoiceData;
              }),
            };
          });

          return {
            transaction_id: transactionData.transaction_id || "",
            customers: customers,
            date: transactionData.date,
            account_id: zoho_bankaccountid!,
            category_type: "split_customer",
          };
        }

      case "Transfer From Another Account":
        return {
          transaction_id: transactionData.transaction_id || "",
          from_transaction_id: transferFromTransactionId,
          date: transferDate,
          from_account_id: transferFromAccountId,
          to_account_id: zoho_bankaccountid,
          amount: transactionData.amount,
          category_type: "transfer_from_another_account",
          reference_number: transferReferenceNumber || "",
        };

      case "Interest Income": {
        const isTallyBackend = getStoredAccountingBackend() === "tally";
        return {
          transaction_id: transactionData.transaction_id || "",
          to_account_id: zoho_bankaccountid,
          // Tally needs the income GL; Zoho picks interest account itself.
          ...(isTallyBackend
            ? { from_account_id: interestIncomeFromAccount }
            : {}),
          amount: transactionData.amount,
          category_type: "interest_income",
          date: interestIncomeDate,
          payment_mode: interestIncomePaymentMode,
          reference_number: interestIncomeReferenceNumber || undefined,
        };
      }

      case "Other Income":
        return {
          transaction_id: transactionData.transaction_id || "",
          from_account_id: otherIncomeFromAccount,
          to_account_id: zoho_bankaccountid,
          amount: transactionData.amount,
          category_type: "other_income",
          date: otherIncomeDate,
          payment_mode: otherIncomePaymentMode,
          reference_number: otherIncomeReferenceNumber || undefined,
        };

      case "Deposit From Other Accounts":
        if (isDepositSplitMode) {
          return {
            transaction_id: transactionData.transaction_id || "",
            to_account_id: zoho_bankaccountid,
            date: depositDate,
            reference_number: depositReferenceNumber || "",
            customer_id: depositReceivedFrom || undefined,
            splits: depositSplits.map((split) => ({
              from_account_id: split.from_account_id,
              amount: split.amount,
              payment_mode: split.payment_mode,
            })),
          };
        } else {
          return {
            transaction_id: transactionData.transaction_id || "",
            from_account_id: depositFromAccount,
            to_account_id: zoho_bankaccountid,
            amount: transactionData.amount,
            date: depositDate,
            category_type: "deposit_from_other_accounts",
            reference_number: depositReferenceNumber || "",
            payment_mode: depositPaymentMode,
            customer_id: depositReceivedFrom || undefined,
          };
        }

      case "Owners Contribution":
        return {
          transaction_id: transactionData.transaction_id || "",
          from_account_id: ownersContributionFromAccount,
          to_account_id: zoho_bankaccountid,
          category_type: "owners_contribution",
          amount: transactionData.amount,
          date: ownersContributionDate,
          payment_mode: ownersContributionPaymentMode,
          reference_number: ownersContributionReferenceNumber || undefined,
        };

      case "Vendor Payment Refund": {
        const isTallyBackend = getStoredAccountingBackend() === "tally";
        const refundAmount = isTallyBackend
          ? transactionData.amount
          : vendorRefundSelectedPayment?.amount;
        return {
          vendor_id: vendorRefundVendor.vendor_id,
          vendor_name: vendorRefundVendor.vendor_name,
          transaction_id: transactionData.transaction_id || "",
          amount: refundAmount,
          payment: isTallyBackend
            ? {
                payment_id: vendorRefundSelectedPayment?.payment_id || "",
                payment_number:
                  vendorRefundSelectedPayment?.payment_number || "",
                amount: refundAmount,
              }
            : {
                payment_id: vendorRefundSelectedPayment?.payment_id,
                payment_number: vendorRefundSelectedPayment?.payment_number,
                amount: vendorRefundSelectedPayment?.amount,
              },
          category_type: "vendor_payment_refund",
          date: vendorRefundDate,
          payment_mode: vendorRefundPaymentMode,
          reference_number: vendorRefundReferenceNumber || "",
          to_account_id: zoho_bankaccountid,
        };
      }

      case "Credits or Refunds Received":
        return {
          transaction_id: transactionData.transaction_id || "",
          category_type: "credits_or_refund_received",
          date: creditsOrRefundDate,
          payment_mode: creditsOrRefundPaymentMode,
          reference_number: creditsOrRefundReferenceNumber || "",
          from_account_id: creditsOrRefundFromAccountId,
          amount: transactionData.amount,
          vendor_id: creditsOrRefundVendor?.vendor_id ?? undefined,
          vendor_name: creditsOrRefundVendor?.vendor_name ?? undefined,
        };

      case "Customer Expense Refund":
        return {
          transaction_id: transactionData.transaction_id || "",
          from_account_id: selectedExpenseGlAccountId,
          to_account_id: zoho_bankaccountid,
          amount: transactionData.amount,
          category_type: "customer_expense_refund",
          date: transactionData.date,
        };

      default:
        return {};
    }
  };

  const handleMatch = async () => {
    setIsMatching(true);
    clearErrors();

    try {
      // Check if transaction data is available
      if (!transactionData || !zoho_bankaccountid) {
        showError("Missing required transaction data.");
        setFieldError("general", "Missing required transaction data");
        setIsMatching(false);
        return;
      }

      // Closed month: non-Admin/CFO must submit via review
      if (isMonthClosed === true && !isAdminOrCFO) {
        setShowClosedMonthConfirmModal(true);
        setIsMatching(false);
        return;
      }

      // Determine which actions need review (for all categories)
      const needsMatchReview = canMatch && needsReviewForMatch;

      // Category-specific review checks (only for Customer Payment)
      let needsAdvanceReview = false;
      let needsSplitReview = false;
      let totalAdvance = 0;
      let hasMultipleCustomers = false;

      if (selectedCategory === "Customer Payment") {
        totalAdvance = calculateTotalAdvancePayments();
        hasMultipleCustomers = customerPaymentInstances.length > 1;
        needsAdvanceReview =
          totalAdvance > 0 && canAdvance && needsReviewForAdvance;
        needsSplitReview =
          hasMultipleCustomers &&
          canSplitTransaction &&
          needsReviewForSplitTransaction;
      }

      console.log("needsSplitReview", needsSplitReview);

      // Check if any action needs review (for all categories)
      if (needsMatchReview || needsAdvanceReview || needsSplitReview) {
        // Check if review already exists
        if (hasReview) {
          if (reviewStatus === "pending") {
            showWarning(
              "A review request is already pending for this transaction. Please wait for it to be reviewed.",
            );
            setIsMatching(false);
            return;
          } else if (reviewStatus === "rejected") {
            showError(
              "Your review request was rejected. Please contact your manager.",
            );
            setIsMatching(false);
            return;
          }
        } else {
          // No review exists, need to create one
          // Determine primary action code
          // Priority: Split transaction review > Advance review > Match review
          let primaryActionCode: string;
          if (needsSplitReview) {
            // Split transaction needs review - use SPLIT_TRANSACTION action
            primaryActionCode = ACTION_CODES.SPLIT_TRANSACTION;
          } else if (needsAdvanceReview) {
            // Advance payment needs review - use ADVANCE action
            primaryActionCode = ACTION_CODES.ADVANCE;
          } else {
            // Match needs review - use MATCH action
            primaryActionCode = ACTION_CODES.MATCH;
          }

          // Build request body (raw_json) - this matches exactly what we send to API
          const requestBody = buildRequestBodyForCategory(selectedCategory);

          // Build field changes (compare current state vs new state)
          // For now, we'll use a simplified approach - compare with empty/zero state
          const oldData = buildEmptyOldDataForCategory(selectedCategory);
          const newData = requestBody;
          const fieldChanges = buildFieldChangesForCategory(
            selectedCategory,
            oldData,
            newData,
          );

          // Build change details
          const changeDetails: ChangeDetails = {
            old_value: "0",
            new_value:
              totalAdvance > 0 ? totalAdvance.toString() : "Transaction match",
            reason: needsSplitReview
              ? "Split payment requires review"
              : needsAdvanceReview
                ? "Match transaction with advance payment requires review"
                : "Match transaction requires review",
            field_changes: fieldChanges,
            raw_json: requestBody,
          };

          // Show review modal
          setPendingMatchAction({
            action: async () => {
              // After review is approved, retry the match
              await handleMatch();
            },
            changeDetails,
            actionCode: primaryActionCode,
          });
          setIsReviewModalOpen(true);
          setIsMatching(false);
          return;
        }
      }

      // Handle different categories with different API calls
      if (selectedCategory === "Customer Payment") {
        // Check permissions
        if (hasMultipleCustomers && !canSplitTransaction) {
          showError("You do not have permission to split transactions");
          setIsMatching(false);
          return;
        }

        if (totalAdvance > 0 && !canAdvance) {
          showError("You do not have permission to process advance payments");
          setIsMatching(false);
          return;
        }

        if (!canMatch) {
          showError("You do not have permission to match transactions");
          setIsMatching(false);
          return;
        }

        // Handle single instance (existing behavior)
        if (customerPaymentInstances.length === 1) {
          const instance = customerPaymentInstances[0];

          if (!instance.customerId || !instance.customerName) {
            showError("Please select a customer.");
            setFieldError("customer", "Please select a customer");
            setIsMatching(false);
            return;
          }

          const selectedInvoicesWithAmounts = instance.selectedInvoices.filter(
            (item) => item.isSelected && item.amount > 0,
          );
          const selectedInvoicesWithoutAmounts =
            instance.selectedInvoices.filter(
              (item) => item.isSelected && item.amount <= 0,
            );

          if (selectedInvoicesWithoutAmounts.length > 0) {
            showWarning(
              "Please enter amounts for all selected invoices before matching.",
            );
            setFieldError(
              "invoices",
              "Please enter amounts for all selected invoices",
            );
            setIsMatching(false);
            return;
          }

          // Check for foreign currency
          const hasForeign = instance.selectedInvoices.some(
            (item) =>
              item.invoice.currency_code &&
              item.invoice.currency_code !== "INR",
          );

          // Validate bank exchange rate for foreign currency transactions
          if (hasForeign) {
            if (!instance.bankExchangeRate || instance.bankExchangeRate <= 0) {
              showWarning(
                "Please enter the Bank Exchange Rate for foreign currency transactions.",
              );
              setFieldError(
                "bankExchangeRate",
                "Bank Exchange Rate is required for foreign currency transactions",
              );
              setIsMatching(false);
              return;
            }
          }

          const isForeign =
            hasForeign &&
            instance.bankExchangeRate &&
            instance.bankExchangeRate > 0;

          const appliedAmountFC = selectedInvoicesWithAmounts.reduce(
            (sum, item) => sum + item.amount,
            0,
          );
          const foreignForex = isForeign
            ? calculateSingleForeignPaymentForex(
                appliedAmountFC,
                instance.bankExchangeRate!,
                transactionData.amount,
              )
            : null;
          const amountForRequest = foreignForex
            ? foreignForex.amountForZoho
            : roundTo2Decimals(transactionData.amount);

          const requestBody: any = {
            category_type: "customer_payment",
            customer_name: instance.customerName,
            transaction_id: transactionData.transaction_id || "",
            advance_payment: instance.advancePayment,
            on_account_amount: instance.onAccountAmount,
            categorize_in_zoho_json: {
              customer_id: instance.customerId,
              date: transactionData.date,
              invoices: selectedInvoicesWithAmounts.map((item) => {
                if (item.invoice.is_tds_applied === 1) {
                  return {
                    id: item.invoice.id,
                    invoice_id: item.invoice.invoice_id,
                    amount_applied: roundTo2Decimals(item.amount),
                    adjustment_amount: item.adjustmentAmount || 0,
                    invoice_number: item.invoice.invoice_number,
                    total: item.invoice.total,
                    currency_symbol: item.invoice.currency_symbol,
                    balance: item.invoice.balance,
                    status: item.invoice.status,
                    is_tds_applicable: 1,
                    tds_tax_amount: "",
                    tds_section: "",
                    tds_tax_id: "",
                    tds_tax_percentage: "",
                    tds_tax_name: "",
                  };
                }
                return {
                  id: item.invoice.id,
                  invoice_id: item.invoice.invoice_id,
                  amount_applied: roundTo2Decimals(item.amount),
                  adjustment_amount: item.adjustmentAmount || 0,
                  invoice_number: item.invoice.invoice_number,
                  total: item.invoice.total,
                  currency_symbol: item.invoice.currency_symbol,
                  balance: item.invoice.balance,
                  status: item.invoice.status,
                  tds_tax_amount: item.tds_tax_amount,
                  tds_section: item.tds_section,
                  is_tds_applied: item.invoice.is_tds_applied,
                  tds_tax_id: item.tds_tax_id,
                  tds_tax_percentage: item.tds_tax_percentage,
                  tds_tax_name: item.tds_tax_name,
                  tds_line_items: getPersistedTdsLineItems(item),
                };
              }),
              amount: amountForRequest,
              account_id: zoho_bankaccountid,
            },
          };

          if (isForeign && foreignForex) {
            requestBody.categorize_in_zoho_json.exchange_rate =
              foreignForex.exchangeRateForZoho;
            requestBody.categorize_in_zoho_json.bank_charges =
              foreignForex.bankChargesFC;
          }

          const response = await categorizeTransaction(requestBody);

          if (response.success) {
            if (onTransactionMatched && transactionData.transaction_id) {
              onTransactionMatched(
                transactionData.transaction_id,
                transactionData.amount,
              );
            }
            handleClose();
          } else {
            showError(`Failed to categorize transaction: ${response.message}`);
            setFieldError(
              "api",
              response.message || "Failed to categorize transaction",
            );
          }
        } else {
          // Handle multiple instances
          // Validate that all instances have customers selected
          const instancesWithoutCustomers = customerPaymentInstances.filter(
            (instance) => !instance.customerId || !instance.customerName,
          );
          if (instancesWithoutCustomers.length > 0) {
            showWarning("Please select a customer for all instances.");
            setFieldError(
              "customers",
              "Please select customers for all instances",
            );
            setIsMatching(false);
            return;
          }

          // Validate that all selected invoices have amounts
          const instancesWithIncompleteInvoices =
            customerPaymentInstances.filter((instance) => {
              const selectedWithoutAmounts = instance.selectedInvoices.filter(
                (item) => item.isSelected && item.amount <= 0,
              );
              return selectedWithoutAmounts.length > 0;
            });
          if (instancesWithIncompleteInvoices.length > 0) {
            showWarning(
              "Please enter amounts for all selected invoices before matching.",
            );
            setFieldError(
              "invoices",
              "Please enter amounts for all selected invoices",
            );
            setIsMatching(false);
            return;
          }

          // Validate bank exchange rates for foreign currency instances
          const instancesWithForeignCurrency = customerPaymentInstances.filter(
            (instance) => {
              const hasForeign = instance.selectedInvoices.some(
                (item) =>
                  item.invoice.currency_code &&
                  item.invoice.currency_code !== "INR",
              );
              return (
                hasForeign &&
                (!instance.bankExchangeRate || instance.bankExchangeRate <= 0)
              );
            },
          );
          if (instancesWithForeignCurrency.length > 0) {
            showWarning(
              "Please enter the Bank Exchange Rate for all foreign currency instances.",
            );
            setFieldError(
              "bankExchangeRate",
              "Bank Exchange Rate is required for foreign currency transactions",
            );
            setIsMatching(false);
            return;
          }

          // Validate exact match requirement
          if (!validateMatchRequirements()) {
            const totalAmounts = calculateTotalInvoiceAmounts();
            const totalAdvance = calculateTotalAdvancePayments();
            const totalOnAccount = calculateTotalOnAccountAmounts();
            const total = totalAmounts + totalAdvance + totalOnAccount;
            const transactionAmountValue = transactionData.amount;
            showWarning(
              `Total allocated amount (invoices + adjustments + advance payments) must equal the transaction amount. Current total: ${total.toFixed(
                2,
              )}, Transaction amount: ${transactionAmountValue.toFixed(
                2,
              )}. Please adjust invoice amounts, adjustments, or advance payments.`,
            );
            setFieldError(
              "match",
              "Total allocated amount must equal transaction amount",
            );
            setIsMatching(false);
            return;
          }

          // Build data structure for multiple instances
          const customers = customerPaymentInstances.map((instance) => {
            const selectedInvoicesWithAmounts =
              instance.selectedInvoices.filter(
                (item) => item.isSelected && item.amount > 0,
              );

            // Check if this instance has foreign currency invoices
            const hasForeign = instance.selectedInvoices.some(
              (item) =>
                item.isSelected &&
                item.invoice.currency_code &&
                item.invoice.currency_code !== "INR",
            );

            // Calculate bank charges for foreign currency transactions
            let exchangeRate: number | null = null;
            let bankCharges: number | null = null;

            if (
              hasForeign &&
              instance.bankExchangeRate &&
              instance.bankExchangeRate > 0
            ) {
              exchangeRate =
                Math.trunc(instance.bankExchangeRate * 1000000) / 1000000;

              // Calculate bank charges for this customer
              // Realisation in INR for foreign currency invoices
              const realisationINR = selectedInvoicesWithAmounts.reduce(
                (sum, item) => sum + item.amount * instance.bankExchangeRate!,
                0,
              );

              // This customer's allocated portion of transaction amount (invoices + advance)
              const customerAllocatedINR = selectedInvoicesWithAmounts.reduce(
                (sum, item) => {
                  // For foreign invoices, we need to consider TDS in calculation
                  const invoiceAmountINR =
                    item.amount * instance.bankExchangeRate!;
                  const tdsAmount =
                    item.invoice.is_tds_applied === 1
                      ? 0
                      : (item.tds_tax_amount || 0) * instance.bankExchangeRate!;
                  return sum + invoiceAmountINR + tdsAmount;
                },
                instance.advancePayment + instance.onAccountAmount, // leftover is already in INR
              );

              // Bank charges = difference between realisation and allocated amount
              const calculatedBankCharges = Math.abs(
                realisationINR - customerAllocatedINR,
              );

              if (calculatedBankCharges > 0.01) {
                bankCharges =
                  Math.trunc(
                    (calculatedBankCharges / instance.bankExchangeRate!) * 100,
                  ) / 100;
              }
            }

            return {
              customer_id: instance.customerId!,
              customer_name: instance.customerName!,
              advance_payment: instance.advancePayment,
              on_account_amount: instance.onAccountAmount,
              exchange_rate: exchangeRate,
              bank_charges: bankCharges,
              invoices: selectedInvoicesWithAmounts.map((item) => {
                const invoiceData: any = {
                  id: item.invoice.id,
                  invoice_id: item.invoice.invoice_id,
                  invoice_number: item.invoice.invoice_number,
                  amount_applied: roundTo2Decimals(item.amount),
                  adjustment_amount: item.adjustmentAmount || 0,
                  total: item.invoice.total,
                  balance: item.invoice.balance,
                  currency_symbol: item.invoice.currency_symbol,
                  status: item.invoice.status,
                };

                // Add currency_code if present
                if (item.invoice.currency_code) {
                  invoiceData.currency_code = item.invoice.currency_code;
                }

                // Handle TDS fields
                if (item.invoice.is_tds_applied === 1) {
                  invoiceData.is_tds_applied = 1;
                  invoiceData.tds_tax_amount = "";
                  invoiceData.tds_section = "";
                  invoiceData.tds_tax_id = "";
                  invoiceData.tds_tax_percentage = "";
                  invoiceData.tds_tax_name = "";
                } else {
                  invoiceData.is_tds_applied = item.invoice.is_tds_applied || 0;
                  if (item.tds_tax_amount && item.tds_tax_amount > 0) {
                    invoiceData.tds_tax_amount = item.tds_tax_amount;
                    invoiceData.tds_section = item.tds_section || "";
                    invoiceData.tds_tax_id = item.tds_tax_id;
                    invoiceData.tds_tax_percentage = item.tds_tax_percentage;
                    invoiceData.tds_tax_name = item.tds_tax_name;
                  }
                }

                const persistedTdsLineItems = getPersistedTdsLineItems(item);
                if (persistedTdsLineItems.length > 0) {
                  invoiceData.tds_line_items = persistedTdsLineItems;
                }

                return invoiceData;
              }),
            };
          });

          // Build request body for multiple customers
          const requestBody = {
            transaction_id: transactionData.transaction_id || "",
            customers: customers,
            date: transactionData.date,
            account_id: zoho_bankaccountid!,
            category_type: "split_customer",
          };

          try {
            const response =
              await categorizeMultipleCustomersTransaction(requestBody);

            if (response.success) {
              showSuccess(
                "Transaction matched successfully for multiple customers!",
              );
              if (onTransactionMatched && transactionData.transaction_id) {
                onTransactionMatched(
                  transactionData.transaction_id,
                  transactionData.amount,
                );
              }
              handleClose();
            } else {
              showError(
                `Failed to categorize transaction: ${response.message}`,
              );
              setFieldError(
                "api",
                response.message || "Failed to categorize transaction",
              );
            }
          } catch (error: any) {
            showError(
              `Failed to categorize transaction: ${
                error.message || "Unknown error occurred"
              }`,
            );
            setFieldError(
              "api",
              error.message || "Failed to categorize transaction",
            );
          } finally {
            setIsMatching(false);
          }
        }
      } else if (selectedCategory === "Transfer From Another Account") {
        // Validate Transfer specific requirements
        if (!transferFromAccountId) {
          showWarning("Please select the 'From Account'.");
          setFieldError(
            "transferFromAccountId",
            "Please select the 'From Account'",
          );
          setIsMatching(false);
          return;
        }

        if (!transferDate) {
          showWarning("Please enter the transfer date.");
          setFieldError("transferDate", "Please enter the transfer date");
          setIsMatching(false);
          return;
        }

        // Build Transfer request body using buildRequestBodyForCategory
        const transferRequestBody =
          buildRequestBodyForCategory(selectedCategory);

        console.log(
          "Sending transfer request:",
          JSON.stringify(transferRequestBody, null, 2),
        );

        const response = await categorizeCustomerTransfer(transferRequestBody);

        if (response.success) {
          if (onTransactionMatched && transactionData.transaction_id) {
            onTransactionMatched(
              transactionData.transaction_id,
              transactionData.amount,
            );
          }
          handleClose();
        } else {
          showError(
            `Failed to categorize transfer: ${
              response.message || "Unknown error"
            }`,
          );
          setFieldError(
            "api",
            response.message || "Failed to categorize transfer",
          );
        }
      } else if (selectedCategory === "Interest Income") {
        // Validate Interest Income specific requirements
        const isTallyBackend = getStoredAccountingBackend() === "tally";
        if (isTallyBackend && !interestIncomeFromAccount) {
          showWarning("Please select the 'From Account'.");
          setFieldError(
            "interestIncomeFromAccount",
            "Please select the 'From Account'",
          );
          setIsMatching(false);
          return;
        }
        if (!interestIncomeDate) {
          showWarning("Please enter the date.");
          setFieldError("interestIncomeDate", "Please enter the date");
          setIsMatching(false);
          return;
        }

        // Build Interest Income request body using buildRequestBodyForCategory
        const interestIncomeRequestBody =
          buildRequestBodyForCategory(selectedCategory);

        console.log(
          "Sending interest income request:",
          JSON.stringify(interestIncomeRequestBody, null, 2),
        );

        const response = await categorizeInterestIncome(
          interestIncomeRequestBody,
        );

        if (response.success) {
          if (onTransactionMatched && transactionData.transaction_id) {
            onTransactionMatched(
              transactionData.transaction_id,
              transactionData.amount,
            );
          }
          handleClose();
        } else {
          showError(
            `Failed to categorize interest income: ${
              response.message || "Unknown error"
            }`,
          );
          setFieldError(
            "api",
            response.message || "Failed to categorize interest income",
          );
        }
      } else if (selectedCategory === "Other Income") {
        // Validate Other Income specific requirements
        if (!otherIncomeFromAccount) {
          showWarning("Please select the 'From Account'.");
          setFieldError(
            "otherIncomeFromAccount",
            "Please select the 'From Account'",
          );
          setIsMatching(false);
          return;
        }

        if (!otherIncomeDate) {
          showWarning("Please enter the date.");
          setFieldError("otherIncomeDate", "Please enter the date");
          setIsMatching(false);
          return;
        }

        // Build Other Income request body using buildRequestBodyForCategory
        const otherIncomeRequestBody =
          buildRequestBodyForCategory(selectedCategory);

        console.log(
          "Sending other income request:",
          JSON.stringify(otherIncomeRequestBody, null, 2),
        );

        const response = await categorizeOtherIncome(otherIncomeRequestBody);

        if (response.success) {
          if (onTransactionMatched && transactionData.transaction_id) {
            onTransactionMatched(
              transactionData.transaction_id,
              transactionData.amount,
            );
          }
          handleClose();
        } else {
          showError(
            `Failed to categorize other income: ${
              response.message || "Unknown error"
            }`,
          );
          setFieldError(
            "api",
            response.message || "Failed to categorize other income",
          );
        }
      } else if (selectedCategory === "Deposit From Other Accounts") {
        if (isDepositSplitMode) {
          // Validate Split Deposit requirements
          if (!depositDate) {
            showWarning("Please enter the date.");
            setFieldError("depositDate", "Please enter the date");
            setIsMatching(false);
            return;
          }

          if (!depositSplits || depositSplits.length === 0) {
            showWarning("Please add at least one split payment.");
            setIsMatching(false);
            return;
          }

          // Validate all splits have required fields
          const invalidSplits = depositSplits.filter(
            (split) =>
              !split.from_account_id ||
              !split.amount ||
              split.amount <= 0 ||
              !split.payment_mode,
          );

          if (invalidSplits.length > 0) {
            showWarning(
              "Please fill in all required fields for each split payment.",
            );
            setIsMatching(false);
            return;
          }

          // Validate total amount matches transaction amount
          const totalSplitAmount = depositSplits.reduce(
            (sum, split) => sum + (split.amount || 0),
            0,
          );
          const transactionAmount = transactionData?.amount || 0;
          const difference = Math.abs(totalSplitAmount - transactionAmount);

          if (difference >= 0.01) {
            showWarning(
              `Total split amount (₹${totalSplitAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}) does not match transaction amount (₹${transactionAmount.toLocaleString(
                "en-IN",
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                },
              )}).`,
            );
            setIsMatching(false);
            return;
          }

          // Build Split Deposit request body
          const depositSplitRequestBody =
            buildRequestBodyForCategory(selectedCategory);

          console.log(
            "Sending customer deposit split request:",
            JSON.stringify(depositSplitRequestBody, null, 2),
          );

          const response = await categorizeCustomerDepositSplit(
            depositSplitRequestBody,
          );

          if (response.success) {
            if (onTransactionMatched && transactionData.transaction_id) {
              onTransactionMatched(
                transactionData.transaction_id,
                transactionData.amount,
              );
            }
            handleClose();
          } else {
            showError(
              `Failed to categorize customer deposit split: ${
                response.message || "Unknown error"
              }`,
            );
            setFieldError(
              "api",
              response.message || "Failed to categorize customer deposit split",
            );
          }
        } else {
          // Validate Deposit From Other Accounts specific requirements
          if (!depositFromAccount) {
            showWarning("Please select the 'From Account'.");
            setFieldError(
              "depositFromAccount",
              "Please select the 'From Account'",
            );
            setIsMatching(false);
            return;
          }

          if (!depositDate) {
            showWarning("Please enter the date.");
            setFieldError("depositDate", "Please enter the date");
            setIsMatching(false);
            return;
          }

          // Build Deposit request body using buildRequestBodyForCategory
          const depositRequestBody =
            buildRequestBodyForCategory(selectedCategory);

          console.log(
            "Sending customer deposit request:",
            JSON.stringify(depositRequestBody, null, 2),
          );

          const response = await categorizeCustomerDeposit(depositRequestBody);

          if (response.success) {
            if (onTransactionMatched && transactionData.transaction_id) {
              onTransactionMatched(
                transactionData.transaction_id,
                transactionData.amount,
              );
            }
            handleClose();
          } else {
            showError(
              `Failed to categorize customer deposit: ${
                response.message || "Unknown error"
              }`,
            );
            setFieldError(
              "api",
              response.message || "Failed to categorize customer deposit",
            );
          }
        }
      } else if (selectedCategory === "Customer Expense Refund") {
        if (!selectedExpenseGlAccountId) {
          showWarning("Please select an expense GL.");
          setFieldError("expenseGl", "Please select an expense GL");
          setIsMatching(false);
          return;
        }

        // Build Customer Expense Refund request body using buildRequestBodyForCategory
        const expenseRequestBody =
          buildRequestBodyForCategory(selectedCategory);

        const response =
          await categorizeCustomerExpenseRefund(expenseRequestBody);

        if (response.success) {
          if (onTransactionMatched && transactionData.transaction_id) {
            onTransactionMatched(
              transactionData.transaction_id,
              transactionData.amount,
            );
          }
          handleClose();
        } else {
          showError(
            `Failed to categorize customer expense refund: ${
              response.message || "Unknown error"
            }`,
          );
          setFieldError(
            "api",
            response.message || "Failed to categorize customer expense refund",
          );
        }
      } else if (selectedCategory === "Owners Contribution") {
        // Validate Owners Contribution specific requirements
        if (!ownersContributionFromAccount) {
          showWarning("Please select the 'From Account'.");
          setFieldError(
            "ownersContributionFromAccount",
            "Please select the 'From Account'",
          );
          setIsMatching(false);
          return;
        }

        if (!ownersContributionDate) {
          showWarning("Please enter the date.");
          setFieldError("ownersContributionDate", "Please enter the date");
          setIsMatching(false);
          return;
        }

        // Build Owners Contribution request body using buildRequestBodyForCategory
        const ownersContributionRequestBody =
          buildRequestBodyForCategory(selectedCategory);

        console.log(
          "Sending owners contribution request:",
          JSON.stringify(ownersContributionRequestBody, null, 2),
        );

        const response = await categorizeOwnersContribution(
          ownersContributionRequestBody,
        );

        if (response.success) {
          if (onTransactionMatched && transactionData.transaction_id) {
            onTransactionMatched(
              transactionData.transaction_id,
              transactionData.amount,
            );
          }
          handleClose();
        } else {
          showError(
            `Failed to categorize owners contribution: ${
              response.message || "Unknown error"
            }`,
          );
          setFieldError(
            "api",
            response.message || "Failed to categorize owners contribution",
          );
        }
      } else if (selectedCategory === "Vendor Payment Refund") {
        const isTallyBackend = getStoredAccountingBackend() === "tally";
        // Validate Vendor Payment Refund specific requirements
        if (!vendorRefundVendor?.vendor_id) {
          showWarning("Please select a vendor.");
          setFieldError("vendorRefundVendorId", "Please select a vendor");
          setIsMatching(false);
          return;
        }

        if (!vendorRefundDate) {
          showWarning("Please enter the date.");
          setFieldError("vendorRefundDate", "Please enter the date");
          setIsMatching(false);
          return;
        }

        if (
          isTallyBackend &&
          !vendorRefundReferenceNumber?.trim()
        ) {
          showWarning("Please enter a Reference #.");
          setFieldError(
            "vendorRefundReferenceNumber",
            "Reference # is required for Tally",
          );
          setIsMatching(false);
          return;
        }

        if (!isTallyBackend && !vendorRefundSelectedPayment) {
          showWarning("Please select a payment with an amount.");
          setFieldError(
            "vendorRefundSelectedPayment",
            "Please select a payment",
          );
          setIsMatching(false);
          return;
        }

        // Build Vendor Payment Refund request body using buildRequestBodyForCategory
        const vendorRefundRequestBody =
          buildRequestBodyForCategory(selectedCategory);

        console.log(
          "Sending vendor payment refund request:",
          JSON.stringify(vendorRefundRequestBody, null, 2),
        );

        const response = await categorizeVendorPaymentRefund(
          vendorRefundRequestBody,
        );

        if (response.success) {
          if (onTransactionMatched && transactionData.transaction_id) {
            onTransactionMatched(
              transactionData.transaction_id,
              transactionData.amount,
            );
          }
          handleClose();
        } else {
          showError(
            `Failed to categorize vendor payment refund: ${
              response.message || "Unknown error"
            }`,
          );
          setFieldError(
            "api",
            response.message || "Failed to categorize vendor payment refund",
          );
        }
      } else if (selectedCategory === "Credits or Refunds Received") {
        if (!creditsOrRefundFromAccountId) {
          showWarning("Please select From Account.");
          setFieldError(
            "creditsOrRefundFromAccountId",
            "From Account is required",
          );
          setIsMatching(false);
          return;
        }
        if (!creditsOrRefundDate) {
          showWarning("Please enter the date.");
          setFieldError("creditsOrRefundDate", "Date is required");
          setIsMatching(false);
          return;
        }
        const creditsRequestBody =
          buildRequestBodyForCategory(selectedCategory);
        const response =
          await categorizeCreditsOrRefundReceived(creditsRequestBody);
        if (response.success) {
          if (onTransactionMatched && transactionData.transaction_id) {
            onTransactionMatched(
              transactionData.transaction_id,
              transactionData.amount,
            );
          }
          handleClose();
        } else {
          showError(
            `Failed to categorize credits or refund received: ${response.message || "Unknown error"}`,
          );
          setFieldError(
            "api",
            response.message ||
              "Failed to categorize credits or refund received",
          );
        }
      }
    } catch (error) {
      console.error("Error processing transaction:", error);
      const userFriendlyError = extractErrorMessage(error);
      showError(`Failed to process transaction: ${userFriendlyError}`);
      setApiErrors(error);
    } finally {
      setIsMatching(false);
    }
  };

  // Check if there are validation errors
  const hasValidationErrors = () => {
    if (selectedCategory === "Customer Payment") {
      // For multiple instances, check validation
      if (customerPaymentInstances.length > 1) {
        // Check if all instances have customers
        const instancesWithoutCustomers = customerPaymentInstances.filter(
          (instance) => !instance.customerId || !instance.customerName,
        );
        if (instancesWithoutCustomers.length > 0) {
          return true;
        }

        // Check if exact match requirement is met
        return !validateMatchRequirements();
      } else {
        // Single instance validation
        const instance = customerPaymentInstances[0];
        if (!instance.customerId || !instance.customerName) {
          return true;
        }

        // Validate that selected invoices have amounts entered
        const selectedInvoicesWithoutAmounts = instance.selectedInvoices.filter(
          (item) => item.isSelected && item.amount <= 0,
        );
        return selectedInvoicesWithoutAmounts.length > 0;
      }
    } else if (selectedCategory === "Transfer From Another Account") {
      // Validate required fields for transfer and that transaction exists
      return (
        !transferFromAccountId || !transferDate || !transferTransactionExists
      );
    } else if (selectedCategory === "Interest Income") {
      // Zoho: date only. Tally: also require income GL (From Account).
      if (getStoredAccountingBackend() === "tally") {
        return !interestIncomeFromAccount || !interestIncomeDate;
      }
      return !interestIncomeDate;
    } else if (selectedCategory === "Other Income") {
      // Validate required fields for other income
      return !otherIncomeFromAccount || !otherIncomeDate;
    } else if (selectedCategory === "Deposit From Other Accounts") {
      // Validate required fields for deposit from other accounts
      if (isDepositSplitMode) {
        // For split mode, validate splits
        if (!depositDate) return true;
        if (!depositSplits || depositSplits.length === 0) return true;

        // Check if all splits are valid
        const hasInvalidSplit = depositSplits.some(
          (split) =>
            !split.from_account_id ||
            !split.amount ||
            split.amount <= 0 ||
            !split.payment_mode,
        );
        if (hasInvalidSplit) return true;

        // Check if total matches transaction amount
        const totalSplitAmount = depositSplits.reduce(
          (sum, split) => sum + (split.amount || 0),
          0,
        );
        const transactionAmount = transactionData?.amount || 0;
        return Math.abs(totalSplitAmount - transactionAmount) >= 0.01;
      } else {
        return !depositFromAccount || !depositDate;
      }
    } else if (selectedCategory === "Owners Contribution") {
      // Validate required fields for owners contribution
      return !ownersContributionFromAccount || !ownersContributionDate;
    } else if (selectedCategory === "Customer Expense Refund") {
      return !selectedExpenseGlAccountId;
    } else if (selectedCategory === "Vendor Payment Refund") {
      // Validate required fields for vendor payment refund
      if (!vendorRefundVendor?.vendor_id || !vendorRefundDate) {
        return true;
      }
      if (getStoredAccountingBackend() === "tally") {
        return !vendorRefundReferenceNumber?.trim();
      }
      // Zoho: payment selection required
      return !vendorRefundSelectedPayment;
    } else if (selectedCategory === "Credits or Refunds Received") {
      return !creditsOrRefundFromAccountId || !creditsOrRefundDate;
    }

    return false;
  };

  const handleCancel = () => {
    handleClose();
  };

  // Helper function to check if selected category matches the zoho_category from transaction
  const shouldUseZohoGl = (category: string): boolean => {
    const categoryToZohoMap: Record<string, string> = {
      "Customer Payment": "customer_payment",
      "Transfer From Another Account": "transfer_from_another_account",
      "Interest Income": "interest_income",
      "Other Income": "other_income",
      "Deposit From Other Accounts": "deposit_from_other_accounts",
      "Owners Contribution": "owners_contribution",
      "Customer Expense Refund": "customer_expense_refund",
      "Vendor Payment Refund": "vendor_payment_refund",
      "Credits or Refunds Received": "credits_or_refund_received",
    };

    const expectedZohoCategory = categoryToZohoMap[category];
    const actualZohoCategory =
      transactionData?.zoho_category || transactionData?.category_type;

    return expectedZohoCategory === actualZohoCategory;
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setCategoryFromSuggestion(false); // User changed; no longer showing recommendation
    if (category !== "Customer Expense Refund") {
      setSelectedExpenseGlAccountId("");
      setSelectedExpenseGlName("");
    }
    // Reset split mode when category changes
    if (category !== "Deposit From Other Accounts") {
      setIsDepositSplitMode(false);
      setDepositSplits([]);
    }
    setFormChanges((prev) => ({
      ...prev,
      category,
    }));
  };

  // Check if we're in view-only mode (review pending or approved)
  const isViewOnlyMode = (): boolean => {
    return isKnownUnderReview;
  };

  // Get raw_json from review
  const getReviewRawJson = (): any => {
    return reviewForPopulation?.change_details?.raw_json || null;
  };

  // View component for customer_payment / split_customer category
  const renderCustomerPaymentView = () => {
    const rawJson = getReviewRawJson();
    if (
      !rawJson ||
      (rawJson.category_type !== "customer_payment" &&
        rawJson.category_type !== "split_customer")
    ) {
      return null;
    }

    // Handle multiple customers (split transaction)
    if (rawJson.customers && Array.isArray(rawJson.customers)) {
      return (
        <div className="flex w-full flex-col items-start gap-4">
          {rawJson.customers.map((customer: any, customerIndex: number) => {
            const invoices = customer.invoices || [];
            const customerName = customer.customer_name || "N/A";
            const categorizeData = customer.categorize_in_zoho_json || customer;
            const amountReceived = categorizeData?.amount || 0;
            const advancePayment = customer.advance_payment || 0;
            const onAccountAmount = customer.on_account_amount || 0;
            const currencySymbol =
              customer.currency_symbol ||
              categorizeData?.currency_symbol ||
              transactionData?.currency_symbol ||
              "₹";

            // Amount used for payments = sum of amount_applied from invoices
            const amountUsedForPayments = invoices.reduce(
              (sum: number, inv: any) => sum + (inv.amount_applied || 0),
              0,
            );

            // TDS Amount = sum of tds_tax_amount from invoices (where is_tds_applied is true/1)
            const tdsAmount = invoices.reduce(
              (sum: number, inv: any) =>
                sum +
                (inv.is_tds_applied === 1 || inv.is_tds_applied === true
                  ? inv.tds_tax_amount || 0
                  : 0),
              0,
            );

            return (
              <div
                key={customerIndex}
                className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4"
              >
                <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border pb-2">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Customer {customerIndex + 1}
                  </span>
                  <span className="text-body font-body text-default-font">
                    {customerName}
                  </span>
                </div>

                {invoices.length > 0 && (
                  <div className="flex w-full flex-col items-start gap-3">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Invoices ({invoices.length})
                    </span>
                    <div className="flex w-full flex-col items-start gap-3">
                      {invoices.map((invoice: any, index: number) => (
                        <div
                          key={index}
                          className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-white px-4 py-3"
                        >
                          <div className="flex w-full items-center justify-between">
                            <span className="text-body font-body text-default-font">
                              {invoice.invoice_number || `Invoice ${index + 1}`}
                            </span>
                            <span className="text-body-bold font-body-bold text-default-font">
                              {currencySymbol}
                              {formatAmount(invoice.amount_applied || 0)}
                            </span>
                          </div>
                          {invoice.tds_tax_id &&
                            invoice.tds_tax_id !== "" &&
                            invoice.tds_tax_amount &&
                            invoice.tds_tax_amount > 0 && (
                              <div className="flex w-full flex-col items-start gap-1 rounded-md bg-neutral-100 px-3 py-2">
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    TDS Section:
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {invoice.tds_section || "N/A"}
                                  </span>
                                </div>
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    TDS Tax Name:
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {invoice.tds_tax_name || "N/A"}
                                  </span>
                                </div>
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    TDS Percentage:
                                  </span>
                                  <span className="text-body font-body text-default-font">
                                    {invoice.tds_tax_percentage || 0}%
                                  </span>
                                </div>
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body font-body text-subtext-color">
                                    TDS Amount:
                                  </span>
                                  <span className="text-body-bold font-body-bold text-default-font">
                                    {currencySymbol}
                                    {formatAmount(invoice.tds_tax_amount || 0)}
                                  </span>
                                </div>
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary Section */}
                <div className="flex w-full flex-col items-start gap-3 border-t border-solid border-neutral-border pt-4 mt-2">
                  {rawJson.category_type !== "split_customer" && (
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Amount Received
                      </span>
                      <span className="text-body font-body text-default-font">
                        {currencySymbol}
                        {formatAmount(amountReceived)}
                      </span>
                    </div>
                  )}
                  <div className="flex w-full items-center justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Amount used for Payments
                    </span>
                    <span className="text-body font-body text-success-600">
                      {currencySymbol}
                      {formatAmount(amountUsedForPayments)}
                    </span>
                  </div>
                  <div className="flex w-full items-center justify-between">
                    <span className="text-body font-body text-subtext-color">
                      TDS Amount
                    </span>
                    <span className="text-body font-body text-blue-600">
                      {currencySymbol}
                      {formatAmount(tdsAmount)}
                    </span>
                  </div>
                  <div className="flex w-full items-center justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Advance Payment
                    </span>
                    <span className="text-body font-body text-default-font">
                      {currencySymbol}
                      {formatAmount(advancePayment)}
                    </span>
                  </div>
                  {onAccountAmount > 0 && (
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-subtext-color">
                        Opening balance
                      </span>
                      <span className="text-body font-body text-default-font">
                        {currencySymbol}
                        {formatAmount(onAccountAmount)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Handle single customer
    const categorizeData = rawJson.categorize_in_zoho_json;
    const invoices = categorizeData?.invoices || [];
    const customerName = rawJson.customer_name || "N/A";
    const amountReceived = categorizeData?.amount || 0;
    const advancePayment = rawJson.advance_payment || 0;
    const onAccountAmount = rawJson.on_account_amount || 0;
    const currencySymbol =
      categorizeData?.currency_symbol ||
      transactionData?.currency_symbol ||
      "₹";

    // Amount used for payments = sum of amount_applied from invoices
    const amountUsedForPayments = invoices.reduce(
      (sum: number, inv: any) => sum + (inv.amount_applied || 0),
      0,
    );

    // TDS Amount = sum of tds_tax_amount from invoices (where is_tds_applied is true/1)
    const tdsAmount = invoices.reduce(
      (sum: number, inv: any) =>
        sum +
        (inv.is_tds_applied === 1 || inv.is_tds_applied === true
          ? inv.tds_tax_amount || 0
          : 0),
      0,
    );

    return (
      <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body-bold font-body-bold text-default-font">
              Customer
            </span>
            <span className="text-body font-body text-default-font">
              {customerName}
            </span>
          </div>

          {invoices.length > 0 && (
            <div className="flex w-full flex-col items-start gap-3">
              <span className="text-body-bold font-body-bold text-default-font">
                Invoices ({invoices.length})
              </span>
              <div className="flex w-full flex-col items-start gap-3">
                {invoices.map((invoice: any, index: number) => (
                  <div
                    key={index}
                    className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-white px-4 py-3"
                  >
                    <div className="flex w-full items-center justify-between">
                      <span className="text-body font-body text-default-font">
                        {invoice.invoice_number || `Invoice ${index + 1}`}
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {currencySymbol}
                        {formatAmount(invoice.amount_applied || 0)}
                      </span>
                    </div>
                    {invoice.tds_tax_id &&
                      invoice.tds_tax_id !== "" &&
                      invoice.tds_tax_amount &&
                      invoice.tds_tax_amount > 0 && (
                        <div className="flex w-full flex-col items-start gap-1 rounded-md bg-neutral-100 px-3 py-2">
                          <div className="flex w-full items-center justify-between">
                            <span className="text-body font-body text-subtext-color">
                              TDS Section:
                            </span>
                            <span className="text-body font-body text-default-font">
                              {invoice.tds_section || "N/A"}
                            </span>
                          </div>
                          <div className="flex w-full items-center justify-between">
                            <span className="text-body font-body text-subtext-color">
                              TDS Tax Name:
                            </span>
                            <span className="text-body font-body text-default-font">
                              {invoice.tds_tax_name || "N/A"}
                            </span>
                          </div>
                          <div className="flex w-full items-center justify-between">
                            <span className="text-body font-body text-subtext-color">
                              TDS Percentage:
                            </span>
                            <span className="text-body font-body text-default-font">
                              {invoice.tds_tax_percentage || 0}%
                            </span>
                          </div>
                          <div className="flex w-full items-center justify-between">
                            <span className="text-body font-body text-subtext-color">
                              TDS Amount:
                            </span>
                            <span className="text-body-bold font-body-bold text-default-font">
                              {currencySymbol}
                              {formatAmount(invoice.tds_tax_amount || 0)}
                            </span>
                          </div>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary Section */}
          <div className="flex w-full flex-col items-start gap-3 border-t border-solid border-neutral-border pt-4 mt-2">
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Amount Received
              </span>
              <span className="text-body font-body text-default-font">
                {currencySymbol}
                {formatAmount(amountReceived)}
              </span>
            </div>
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Amount used for Payments
              </span>
              <span className="text-body font-body text-success-600">
                {currencySymbol}
                {formatAmount(amountUsedForPayments)}
              </span>
            </div>
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                TDS Amount
              </span>
              <span className="text-body font-body text-blue-600">
                {currencySymbol}
                {formatAmount(tdsAmount)}
              </span>
            </div>
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Advance Payment
              </span>
              <span className="text-body font-body text-default-font">
                {currencySymbol}
                {formatAmount(advancePayment)}
              </span>
            </div>
            {onAccountAmount > 0 && (
              <div className="flex w-full items-center justify-between">
                <span className="text-body font-body text-subtext-color">
                  Opening balance
                </span>
                <span className="text-body font-body text-default-font">
                  {currencySymbol}
                  {formatAmount(onAccountAmount)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // View component for other categories
  const renderOtherCategoryView = () => {
    const rawJson = getReviewRawJson();
    if (!rawJson) {
      return null;
    }

    const categoryType = rawJson.category_type;

    if (categoryType === "transfer_from_another_account") {
      const fromAccount = allBankAccounts.find(
        (acc) => acc.zoho_bankaccountid === rawJson.from_account_id,
      );
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              From Account:
            </span>
            <span className="text-body font-body text-default-font">
              {fromAccount?.bank_name || rawJson.from_account_id || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "interest_income") {
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Payment Mode:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.payment_mode || "N/A"}
            </span>
          </div>
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "other_income") {
      const fromAccount = allBankAccounts.find(
        (acc) => acc.zoho_bankaccountid === rawJson.from_account_id,
      );
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              From Account:
            </span>
            <span className="text-body font-body text-default-font">
              {fromAccount?.bank_name || rawJson.from_account_id || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Payment Mode:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.payment_mode || "N/A"}
            </span>
          </div>
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "deposit_from_other_accounts") {
      const fromAccount = allBankAccounts.find(
        (acc) => acc.zoho_bankaccountid === rawJson.from_account_id,
      );
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              From Account:
            </span>
            <span className="text-body font-body text-default-font">
              {fromAccount?.bank_name || rawJson.from_account_id || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Payment Mode:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.payment_mode || "N/A"}
            </span>
          </div>
          {rawJson.customer_id && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Received From:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.customer_id}
              </span>
            </div>
          )}
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "deposit_split") {
      const splits = rawJson.splits ?? [];
      const currencySymbol =
        rawJson.currency_symbol ?? transactionData?.currency_symbol ?? "₹";
      return (
        <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full flex-col gap-2">
            <span className="text-heading-4 font-heading-4 text-default-font">
              Deposit From Other Accounts (Split)
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Split across {splits.length} source(s)
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
          {splits.length > 0 && (
            <div className="flex w-full flex-col gap-2">
              <span className="text-body-bold font-body-bold text-default-font">
                Split Payments
              </span>
              <div className="overflow-x-auto rounded border border-neutral-border bg-white">
                <table className="w-full min-w-[280px]">
                  <thead>
                    <tr className="border-b border-neutral-border bg-neutral-50">
                      <th className="px-2 py-1.5 text-left text-caption font-body-bold text-subtext-color">
                        From Account
                      </th>
                      <th className="px-2 py-1.5 text-right text-caption font-body-bold text-subtext-color">
                        Amount
                      </th>
                      <th className="px-2 py-1.5 text-left text-caption font-body-bold text-subtext-color">
                        Payment Mode
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {splits.map((split: any, i: number) => {
                      const fromAccount =
                        allBankAccounts.find(
                          (acc) =>
                            acc.zoho_bankaccountid === split.from_account_id,
                        ) ??
                        (split.from_account_name
                          ? { bank_name: split.from_account_name }
                          : null);
                      return (
                        <tr
                          key={i}
                          className="border-b border-neutral-border last:border-b-0"
                        >
                          <td className="px-2 py-1.5 text-body text-default-font">
                            {fromAccount?.bank_name ??
                              split.from_account_id ??
                              "—"}
                          </td>
                          <td className="px-2 py-1.5 text-right text-body text-default-font whitespace-nowrap">
                            {currencySymbol}{" "}
                            {Number(split.amount ?? 0).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="px-2 py-1.5 text-body text-subtext-color">
                            {split.payment_mode ?? "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex w-full items-center justify-between border-t border-neutral-border pt-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Total
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {currencySymbol}{" "}
                  {Number(
                    rawJson.transaction_amount ??
                      splits.reduce(
                        (s: number, sp: any) => s + (sp.amount ?? 0),
                        0,
                      ),
                  ).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "owners_contribution") {
      const fromAccount = allBankAccounts.find(
        (acc) => acc.zoho_bankaccountid === rawJson.from_account_id,
      );
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              From Account:
            </span>
            <span className="text-body font-body text-default-font">
              {fromAccount?.bank_name || rawJson.from_account_id || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Payment Mode:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.payment_mode || "N/A"}
            </span>
          </div>
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "customer_expense_refund") {
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Expense GL:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.from_account_id || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
        </div>
      );
    }

    if (categoryType === "vendor_payment_refund") {
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Vendor:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.vendor_name || rawJson.vendor_id || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Payment Mode:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.payment_mode || "N/A"}
            </span>
          </div>
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
        </div>
      );
    }

    if (categoryType === "credits_or_refund_received") {
      return (
        <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Date:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.date || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Received Via:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.payment_mode || "N/A"}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              From Account:
            </span>
            <span className="text-body font-body text-default-font">
              {rawJson.from_account_name || rawJson.from_account_id || "N/A"}
            </span>
          </div>
          {(rawJson.vendor_name || rawJson.vendor_id) && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Received From:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.vendor_name || rawJson.vendor_id || "N/A"}
              </span>
            </div>
          )}
          {rawJson.reference_number && (
            <div className="flex w-full items-center justify-between">
              <span className="text-body font-body text-subtext-color">
                Reference Number:
              </span>
              <span className="text-body font-body text-default-font">
                {rawJson.reference_number}
              </span>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  // Old handler functions removed - logic moved to CustomerPaymentReco component

  // Don't render if no transaction data
  if (!transactionData) {
    return null;
  }

  return (
    <DialogLayout open={open} onOpenChange={handleOpenChange}>
      <div className="flex w-256 flex-col items-start gap-6 bg-default-background px-8 py-6">
        <span className="text-heading-2 font-heading-2 text-default-font">
          Review Transaction
        </span>
        {checkHasErrors() && (
          <ErrorBanner
            errors={errors}
            title="Please fix the following errors:"
          />
        )}
        {isMonthClosed === true && <ClosedMonthBanner />}
        <div className="flex w-full flex-col items-start gap-6 rounded-lg border border-solid border-neutral-border bg-neutral-50 px-8 py-6 shadow-sm">
          <div className="flex w-full flex-col items-start gap-4">
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-3">
                <IconWithBackground icon={<FeatherCreditCard />} />
                <span className="text-heading-3 font-heading-3 text-default-font">
                  {transactionData.description}
                </span>
              </div>
              <span className="text-heading-3 font-heading-3 text-success-600">
                {transactionData.currency_symbol || "₹"}
                {formatAmount(transactionData.amount)}
              </span>
            </div>
            <div className="flex w-full flex-wrap items-center gap-6 border-t border-solid border-neutral-border pt-4">
              <div className="flex items-center gap-2">
                <FeatherCalendar className="text-body font-body text-neutral-400" />
                <span className="text-body-bold font-body-bold text-default-font">
                  {new Date(transactionData.date).toLocaleDateString()}
                </span>
              </div>
              {/* <div className="flex items-center gap-2">
                <FeatherHash className="text-body font-body text-neutral-400" />
                <span className="text-body-bold font-body-bold text-default-font">
                  {transactionData.transaction_id}
                </span>
              </div> */}
              <div className="flex items-center gap-2">
                <FeatherActivity className="text-body font-body text-neutral-400" />
                <span className="text-body-bold font-body-bold text-default-font">
                  Deposit
                </span>
              </div>
              <Badge
                variant={
                  reviewStatus === "rejected"
                    ? "error"
                    : transactionData.user_status === "Reconciled"
                      ? "success"
                      : "warning"
                }
                className={
                  reviewStatus === "pending"
                    ? "border border-solid border-purple-200 bg-purple-50 [&>span]:text-purple-700"
                    : undefined
                }
              >
                {reviewStatus === "rejected"
                  ? "Rejected"
                  : reviewStatus === "pending"
                    ? "Sent for Review"
                    : transactionData.user_status}
              </Badge>
            </div>
          </div>
        </div>

        {/* Show rejection reason if review is rejected */}
        {reviewStatus === "rejected" &&
          reviewForPopulation?.review_comments && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md bg-error-50 px-4 py-3 border border-solid border-error-200">
              <span className="text-body-bold font-body-bold text-error-700">
                Review Rejected
              </span>
              <span className="text-body font-body text-error-700">
                {reviewForPopulation.review_comments}
              </span>
            </div>
          )}

        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {isViewOnlyMode() ? "Review Data" : "Manual Categorization"}
            </span>
            {selectedCategory === "Customer Payment" && !isViewOnlyMode() && (
              <Button
                variant="brand-secondary"
                size="small"
                icon={<FeatherPlus />}
                onClick={handleAddCustomer}
              >
                Add Customer
              </Button>
            )}
          </div>
          <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
            {isWaitingForReviewData ? (
              <div className="flex w-full items-center rounded-md border border-solid border-neutral-border bg-default-background px-6 py-4">
                <span className="text-body font-body text-subtext-color">
                  {isReviewDataLoading
                    ? "Loading review data..."
                    : "Review data is not available yet."}
                </span>
              </div>
            ) : isViewOnlyMode() &&
              isSplitTransactionAmountReview(reviewForPopulation) ? (
              <SplitTransactionAmountReviewView
                rawJson={getReviewRawJson()}
                fallbackAmount={transactionData?.amount}
                fallbackCurrency={transactionData?.currency_symbol}
                fallbackDescription={transactionData?.description}
              />
            ) : (
              <>
                <div className="flex w-full items-center gap-16">
                  <div className="flex items-center gap-2">
                    <span className="text-body font-body text-default-font">
                      Category
                    </span>
                    {isViewOnlyMode() ? (
                      <span className="text-body-bold font-body-bold text-default-font">
                        {selectedCategory}
                      </span>
                    ) : (
                      <SubframeCore.DropdownMenu.Root>
                        <SubframeCore.DropdownMenu.Trigger asChild={true}>
                          <Button
                            className="h-8 w-40 flex-none justify-between"
                            variant="neutral-secondary"
                            iconRight={
                              suggestionApplying || isPopulatingFromReview ? (
                                <FeatherLoader className="animate-spin" />
                              ) : (
                                <FeatherChevronDown />
                              )
                            }
                            disabled={
                              suggestionApplying || isPopulatingFromReview
                            }
                            onClick={(
                              event: React.MouseEvent<HTMLButtonElement>,
                            ) => {}}
                          >
                            {suggestionApplying || isPopulatingFromReview
                              ? "Loading..."
                              : truncateText(selectedCategory, 15)}
                          </Button>
                        </SubframeCore.DropdownMenu.Trigger>
                        <SubframeCore.DropdownMenu.Portal>
                          <SubframeCore.DropdownMenu.Content
                            side="bottom"
                            align="start"
                            sideOffset={4}
                            asChild={true}
                          >
                            <div className="max-h-60 overflow-y-auto dropdown-menu-content">
                              <DropdownMenu>
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherDollarSign />}
                                  onClick={() =>
                                    handleCategorySelect("Customer Payment")
                                  }
                                >
                                  Customer Payment
                                </DropdownMenu.DropdownItem>
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherBuilding />}
                                  onClick={() =>
                                    handleCategorySelect(
                                      "Transfer From Another Account",
                                    )
                                  }
                                >
                                  Transfer From Another Account
                                </DropdownMenu.DropdownItem>
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherDollarSign />}
                                  onClick={() =>
                                    handleCategorySelect("Interest Income")
                                  }
                                >
                                  Interest Income
                                </DropdownMenu.DropdownItem>
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherDollarSign />}
                                  onClick={() =>
                                    handleCategorySelect("Other Income")
                                  }
                                >
                                  Other Income
                                </DropdownMenu.DropdownItem>
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherBuilding />}
                                  onClick={() =>
                                    handleCategorySelect(
                                      "Deposit From Other Accounts",
                                    )
                                  }
                                >
                                  Deposit From Other Accounts
                                </DropdownMenu.DropdownItem>
                                {getStoredAccountingBackend() !== "tally" && (
                                  <DropdownMenu.DropdownItem
                                    icon={<FeatherDollarSign />}
                                    onClick={() =>
                                      handleCategorySelect(
                                        "Owners Contribution",
                                      )
                                    }
                                  >
                                    Owners Contribution
                                  </DropdownMenu.DropdownItem>
                                )}
                                {/* <DropdownMenu.DropdownItem
                              icon={<FeatherDollarSign />}
                              onClick={() =>
                                handleCategorySelect("Customer Expense Refund")
                              }
                            >
                              Customer Expense Refund
                            </DropdownMenu.DropdownItem> */}
                                <DropdownMenu.DropdownItem
                                  icon={<FeatherBuilding />}
                                  onClick={() =>
                                    handleCategorySelect(
                                      "Vendor Payment Refund",
                                    )
                                  }
                                >
                                  Vendor Payment Refund
                                </DropdownMenu.DropdownItem>
                                {currentAccountType &&
                                  (currentAccountType.toLowerCase() ===
                                    "credit" ||
                                    currentAccountType.toLowerCase() ===
                                      "credit_card") && (
                                    <DropdownMenu.DropdownItem
                                      icon={<FeatherDollarSign />}
                                      onClick={() =>
                                        handleCategorySelect(
                                          "Credits or Refunds Received",
                                        )
                                      }
                                    >
                                      Credits or Refunds Received
                                    </DropdownMenu.DropdownItem>
                                  )}
                              </DropdownMenu>
                            </div>
                          </SubframeCore.DropdownMenu.Content>
                        </SubframeCore.DropdownMenu.Portal>
                      </SubframeCore.DropdownMenu.Root>
                    )}
                    {!isViewOnlyMode() &&
                      categoryFromSuggestion &&
                      hasSuggestedCategory &&
                      !(
                        suggestedCategoryValue === "customer_payment" &&
                        transactionData?.zoho_partyid === "create_new"
                      ) && <Badge variant="brand">Recommended</Badge>}
                  </div>
                  {selectedCategory === "Customer Expense Refund" &&
                    !isViewOnlyMode() && (
                      <div className="flex items-center gap-2">
                        <span className="text-body font-body text-default-font">
                          Expense GL
                        </span>
                        <BankGlAccountDropdown
                          preset="expense"
                          selectedAccountId={selectedExpenseGlAccountId}
                          selectedAccountName={selectedExpenseGlName}
                          onSelect={(account) => {
                            setSelectedExpenseGlAccountId(
                              String(account.account_id),
                            );
                            setSelectedExpenseGlName(account.gl_name);
                          }}
                          placeholder="Select Expense GL"
                          dropdownClassName="min-w-[16rem]"
                          buttonClassName="h-8 w-64 flex-none overflow-hidden"
                        />
                      </div>
                    )}
                  {/* <div className="flex grow shrink-0 basis-0 items-center justify-end gap-1 self-stretch">
                <IconButton
                  size="small"
                  icon={<FeatherTrash />}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {}}
                />
              </div> */}
                </div>
                {selectedCategory === "Customer Payment" && (
                  <>
                    {isViewOnlyMode() ? (
                      // View-only mode: Show data from raw_json
                      renderCustomerPaymentView()
                    ) : (
                      // Normal mode: Show form components
                      <>
                        {getStoredAccountingBackend() === "tally" && (
                          <Checkbox
                            label="Knock off opening balance"
                            checked={knockOffOpeningBalance}
                            onCheckedChange={(checked) =>
                              setKnockOffOpeningBalance(checked === true)
                            }
                          />
                        )}
                        {/* Render all customer payment instances */}
                        {customerPaymentInstances.map((instance, index) => {
                          const totalFromOthers =
                            calculateTotalAllocatedAmountsFromOtherInstances(
                              instance.id,
                            );
                          // Get excluded customer IDs (all other selected customers)
                          const excludedCustomerIds = customerPaymentInstances
                            .filter(
                              (inst) =>
                                inst.id !== instance.id && inst.customerId,
                            )
                            .map((inst) => inst.customerId!);
                          return (
                            <CustomerPaymentReco
                              key={instance.id}
                              id={instance.id}
                              ref={setInstanceRef(instance.id)}
                              transactionAmount={transactionData?.amount || 0}
                              currencySymbol={
                                instance.selectedInvoices[0]?.invoice
                                  .currency_symbol ||
                                transactionData?.currency_symbol ||
                                "₹"
                              }
                              tdsRates={tdsRates}
                              bankExchangeRate={instance.bankExchangeRate}
                              onBankExchangeRateChange={(rate) => {
                                handleInstanceChange(instance.id, {
                                  customerId: instance.customerId,
                                  customerName: instance.customerName,
                                  selectedInvoices: instance.selectedInvoices,
                                  advancePayment: instance.advancePayment,
                                  onAccountAmount: instance.onAccountAmount,
                                  totalInvoiceAmounts:
                                    instance.totalInvoiceAmounts,
                                  bankExchangeRate: rate,
                                });
                              }}
                              showRemoveButton={
                                customerPaymentInstances.length > 1
                              }
                              onRemove={
                                customerPaymentInstances.length > 1
                                  ? () => handleRemoveCustomer(instance.id)
                                  : undefined
                              }
                              isMultipleInstances={
                                customerPaymentInstances.length > 1
                              }
                              totalAllocatedAmountsFromOtherInstances={
                                totalFromOthers
                              }
                              excludedCustomerIds={excludedCustomerIds}
                              knockOffOpeningBalance={knockOffOpeningBalance}
                              onChange={getInstanceChangeCallback(instance.id)}
                              autoSelectCustomerId={
                                instance.customerId ||
                                (index === 0 &&
                                transactionData?.zoho_partyid &&
                                transactionData?.zoho_partyid !== "create_new"
                                  ? transactionData.zoho_partyid
                                  : undefined)
                              }
                              autoSelectCustomerName={
                                instance.customerName ||
                                (index === 0 &&
                                transactionData?.zoho_partyid_confidence
                                  ? transactionData.description || undefined
                                  : undefined)
                              }
                              onInitialPartyLoaded={
                                index === 0 &&
                                selectedCategory === "Customer Payment" &&
                                hasSuggestedParty
                                  ? () => setInitialPartyLoaded(true)
                                  : undefined
                              }
                              recommendationSource={
                                index === 0
                                  ? transactionData?.zoho_partyid_source
                                  : undefined
                              }
                              recommendationConfidence={
                                index === 0
                                  ? transactionData?.zoho_partyid_confidence
                                  : undefined
                              }
                            />
                          );
                        })}
                      </>
                    )}
                  </>
                )}

                {selectedCategory === "Transfer From Another Account" &&
                  (isViewOnlyMode() ? (
                    renderOtherCategoryView()
                  ) : (
                    <CustomerTransferFromAccountReco
                      currentBankAccountId={currentBankAccountId}
                      currentBankAccountName={currentBankAccountName}
                      allBankAccounts={allBankAccounts}
                      transactionDate={transactionData?.date || ""}
                      transactionAmount={transactionData?.amount || 0}
                      currentTransactionId={
                        transactionData?.transaction_id || ""
                      }
                      onFromAccountChange={setTransferFromAccountId}
                      onDateChange={setTransferDate}
                      onReferenceNumberChange={setTransferReferenceNumber}
                      onTransactionExistsChange={setTransferTransactionExists}
                      onFromTransactionIdChange={setTransferFromTransactionId}
                      zohoGl={
                        shouldUseZohoGl("Transfer From Another Account")
                          ? transactionData?.zoho_gl
                          : undefined
                      }
                    />
                  ))}

                {selectedCategory === "Interest Income" &&
                  (isViewOnlyMode() ? (
                    renderOtherCategoryView()
                  ) : (
                    <CustomerInterestIncomeReco
                      transactionDate={transactionData?.date || ""}
                      onFromAccountChange={setInterestIncomeFromAccount}
                      onDateChange={setInterestIncomeDate}
                      onReferenceNumberChange={setInterestIncomeReferenceNumber}
                      onPaymentModeChange={setInterestIncomePaymentMode}
                      zohoGl={
                        shouldUseZohoGl("Interest Income")
                          ? transactionData?.zoho_gl
                          : undefined
                      }
                    />
                  ))}

                {selectedCategory === "Other Income" &&
                  (isViewOnlyMode() ? (
                    renderOtherCategoryView()
                  ) : (
                    <CustomerOtherIncomeReco
                      transactionDate={transactionData?.date || ""}
                      onFromAccountChange={setOtherIncomeFromAccount}
                      onDateChange={setOtherIncomeDate}
                      onPaymentModeChange={setOtherIncomePaymentMode}
                      onReferenceNumberChange={setOtherIncomeReferenceNumber}
                      zohoGl={
                        shouldUseZohoGl("Other Income")
                          ? transactionData?.zoho_gl
                          : undefined
                      }
                    />
                  ))}

                {selectedCategory === "Deposit From Other Accounts" &&
                  (isViewOnlyMode() ? (
                    renderOtherCategoryView()
                  ) : (
                    <div className="flex w-full flex-col items-start gap-4">
                      <div className="flex w-full items-center justify-end">
                        <Button
                          variant="neutral-secondary"
                          size="small"
                          onClick={() =>
                            setIsDepositSplitMode(!isDepositSplitMode)
                          }
                        >
                          {isDepositSplitMode
                            ? "Single Payment"
                            : "Split Payment"}
                        </Button>
                      </div>
                      {isDepositSplitMode ? (
                        <CustomerDepositFromOtherAccountsSplitReco
                          transactionDate={transactionData?.date || ""}
                          transactionAmount={transactionData?.amount || 0}
                          bankAccountId={zoho_bankaccountid || undefined}
                          onDateChange={setDepositDate}
                          onReceivedFromChange={setDepositReceivedFrom}
                          onReferenceNumberChange={setDepositReferenceNumber}
                          onSplitsChange={setDepositSplits}
                          zohoGl={
                            shouldUseZohoGl("Deposit From Other Accounts")
                              ? transactionData?.zoho_gl
                              : undefined
                          }
                        />
                      ) : (
                        <CustomerDepositFromOtherAccountsReco
                          transactionDate={transactionData?.date || ""}
                          bankAccountId={zoho_bankaccountid || undefined}
                          onDateChange={setDepositDate}
                          onPaymentModeChange={setDepositPaymentMode}
                          onFromAccountChange={setDepositFromAccount}
                          onReceivedFromChange={setDepositReceivedFrom}
                          onReferenceNumberChange={setDepositReferenceNumber}
                          zohoGl={
                            shouldUseZohoGl("Deposit From Other Accounts")
                              ? transactionData?.zoho_gl
                              : undefined
                          }
                        />
                      )}
                    </div>
                  ))}

                {selectedCategory === "Owners Contribution" &&
                  (isViewOnlyMode() ? (
                    renderOtherCategoryView()
                  ) : (
                    <CustomerPaymentOwnersContributionReco
                      transactionDate={transactionData?.date || ""}
                      onFromAccountChange={setOwnersContributionFromAccount}
                      onDateChange={setOwnersContributionDate}
                      onPaymentModeChange={setOwnersContributionPaymentMode}
                      onReferenceNumberChange={
                        setOwnersContributionReferenceNumber
                      }
                      zohoGl={
                        shouldUseZohoGl("Owners Contribution")
                          ? transactionData?.zoho_gl
                          : undefined
                      }
                    />
                  ))}

                {selectedCategory === "Vendor Payment Refund" &&
                  (isViewOnlyMode() ? (
                    renderOtherCategoryView()
                  ) : (
                    <CustomerVendorPaymentReco
                      transactionDate={transactionData?.date || ""}
                      transactionAmount={transactionData?.amount || 0}
                      onVendorChange={setVendorRefundVendor}
                      onDateChange={setVendorRefundDate}
                      onReferenceNumberChange={setVendorRefundReferenceNumber}
                      onPaymentModeChange={setVendorRefundPaymentMode}
                      onSelectedPaymentsChange={setVendorRefundSelectedPayment}
                    />
                  ))}

                {selectedCategory === "Credits or Refunds Received" &&
                  (isViewOnlyMode() ? (
                    renderOtherCategoryView()
                  ) : (
                    <CustomerCreditsOrRefundReceivedReco
                      transactionDate={transactionData?.date || ""}
                      transactionAmount={transactionData?.amount ?? 0}
                      onFromAccountChange={setCreditsOrRefundFromAccountId}
                      onDateChange={setCreditsOrRefundDate}
                      onPaymentModeChange={setCreditsOrRefundPaymentMode}
                      onReferenceNumberChange={
                        setCreditsOrRefundReferenceNumber
                      }
                      onVendorChange={setCreditsOrRefundVendor}
                      zohoGl={transactionData?.zoho_gl}
                    />
                  ))}

                {selectedCategory === "Customer Expense Refund" &&
                  (isViewOnlyMode() ? renderOtherCategoryView() : null)}
              </>
            )}
          </div>

          {!isViewOnlyMode() && (
            <div className="flex w-full flex-col items-start gap-4 rounded-md bg-neutral-50 px-6 py-4">
              <div className="flex w-full items-center justify-between">
                <span className="text-body font-body text-subtext-color">
                  Amount Received{" "}
                  {customerPaymentInstances.some(
                    (instance) =>
                      instance.selectedInvoices.some(
                        (item) =>
                          item.invoice.currency_code &&
                          item.invoice.currency_code !== "INR",
                      ) &&
                      instance.bankExchangeRate &&
                      instance.bankExchangeRate > 0,
                  )
                    ? "(Inclusive of Bank Charges)"
                    : ""}
                </span>
                <span className="text-body-bold font-body-bold text-default-font">
                  {customerPaymentInstances.some((instance) =>
                    instance.selectedInvoices.some(
                      (item) =>
                        item.invoice.currency_code &&
                        item.invoice.currency_code !== "INR",
                    ),
                  )
                    ? "₹"
                    : customerPaymentInstances[0]?.selectedInvoices[0]?.invoice
                        .currency_symbol ||
                      transactionData?.currency_symbol ||
                      "₹"}
                  {formatAmount(transactionData.amount)}
                </span>
              </div>

              {selectedCategory === "Customer Payment" && (
                <>
                  {/* Warning when totals don't match (only show, don't scroll) */}
                  {customerPaymentInstances.length > 1 &&
                    !validateMatchRequirements() && (
                      <div className="w-full rounded-md border border-solid border-error-300 bg-error-50 px-4 py-3">
                        <div className="flex items-start gap-2">
                          <span className="text-body font-body text-error-600">
                            ⚠️ Total allocated amount (invoices + advance
                            payments) does not equal the transaction amount.
                            Please adjust invoice amounts or advance payments.
                          </span>
                        </div>
                      </div>
                    )}
                  {(() => {
                    // Calculate totals across all instances
                    const totalInvoiceAmounts = calculateTotalInvoiceAmounts();
                    const totalAdvancePayments =
                      calculateTotalAdvancePayments();
                    const totalOnAccountAmounts =
                      calculateTotalOnAccountAmounts();

                    // Calculate total TDS amount across all instances
                    const totalTdsAmount = customerPaymentInstances.reduce(
                      (sum, instance) => {
                        return (
                          sum +
                          instance.selectedInvoices
                            .filter(
                              (item) =>
                                item.isSelected &&
                                item.invoice.is_tds_applied !== 1,
                            )
                            .reduce(
                              (subSum, item) =>
                                subSum + (item.tds_tax_amount || 0),
                              0,
                            )
                        );
                      },
                      0,
                    );

                    const currencySymbol =
                      customerPaymentInstances[0]?.selectedInvoices[0]?.invoice
                        .currency_symbol ||
                      transactionData?.currency_symbol ||
                      "₹";

                    // Check if any instance has foreign currency
                    const hasAnyForeign = customerPaymentInstances.some(
                      (instance) =>
                        instance.selectedInvoices.some(
                          (item) =>
                            item.invoice.currency_code &&
                            item.invoice.currency_code !== "INR",
                        ),
                    );

                    // Get bank exchange rate from first instance that has it (for display purposes)
                    const firstBankExchangeRate =
                      customerPaymentInstances.find(
                        (instance) => instance.bankExchangeRate !== null,
                      )?.bankExchangeRate || null;

                    // Show per-instance breakdown if multiple instances
                    if (customerPaymentInstances.length > 1) {
                      return (
                        <>
                          {customerPaymentInstances.map((instance, idx) => {
                            const instanceInvoiceAmounts =
                              instance.totalInvoiceAmounts;
                            const instanceTdsAmount = instance.selectedInvoices
                              .filter(
                                (item) =>
                                  item.isSelected &&
                                  item.invoice.is_tds_applied !== 1,
                              )
                              .reduce(
                                (sum, item) => sum + (item.tds_tax_amount || 0),
                                0,
                              );
                            const instanceAdvancePayment =
                              instance.advancePayment;
                            const instanceOnAccountAmount =
                              instance.onAccountAmount;
                            const instanceCurrencySymbol =
                              instance.selectedInvoices[0]?.invoice
                                .currency_symbol ||
                              transactionData?.currency_symbol ||
                              "₹";

                            return (
                              <div
                                key={instance.id}
                                className="flex w-full flex-col gap-2 border-t border-solid border-neutral-border pt-3 mt-2"
                              >
                                <div className="flex w-full items-center justify-between">
                                  <span className="text-body-bold font-body-bold text-default-font">
                                    Customer {idx + 1}:{" "}
                                    {instance.customerName || "Not Selected"}
                                  </span>
                                </div>
                                <div className="flex w-full items-center justify-between pl-4">
                                  <span className="text-body font-body text-subtext-color">
                                    Amount used for Payments
                                  </span>
                                  <span className="text-body-bold font-body-bold text-success-600">
                                    {instanceCurrencySymbol}
                                    {formatAmount(instanceInvoiceAmounts)}
                                  </span>
                                </div>
                                {instanceTdsAmount > 0 && (
                                  <div className="flex w-full items-center justify-between pl-4">
                                    <span className="text-body font-body text-subtext-color">
                                      TDS Amount
                                    </span>
                                    <span className="text-body-bold font-body-bold text-warning-600">
                                      {instanceCurrencySymbol}
                                      {formatAmount(instanceTdsAmount)}
                                    </span>
                                  </div>
                                )}
                                {instanceOnAccountAmount > 0 && (
                                  <div className="flex w-full items-center justify-between pl-4">
                                    <span className="text-body font-body text-subtext-color">
                                      Opening balance
                                    </span>
                                    <span className="text-body-bold font-body-bold text-default-font">
                                      ₹{formatAmount(instanceOnAccountAmount)}
                                    </span>
                                  </div>
                                )}
                                {instanceAdvancePayment > 0 && (
                                  <div className="flex w-full items-center justify-between pl-4">
                                    <span className="text-body font-body text-subtext-color">
                                      Advance Payment
                                    </span>
                                    <span className="text-body-bold font-body-bold text-default-font">
                                      {hasAnyForeign &&
                                      instance.bankExchangeRate &&
                                      instance.bankExchangeRate > 0
                                        ? `${instanceCurrencySymbol}${formatAmount(
                                            Math.max(0, instanceAdvancePayment),
                                          )}`
                                        : `₹${formatAmount(
                                            Math.max(0, instanceAdvancePayment),
                                          )}`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </>
                      );
                    }

                    // Single instance - show aggregated totals
                    return (
                      <>
                        <div className="flex w-full items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            Amount used for Payments
                          </span>
                          <span className="text-body-bold font-body-bold text-success-600">
                            {currencySymbol}
                            {formatAmount(totalInvoiceAmounts)}
                          </span>
                        </div>

                        <div className="flex w-full items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            TDS Amount
                          </span>
                          <span className="text-body-bold font-body-bold text-warning-600">
                            {currencySymbol}
                            {formatAmount(totalTdsAmount)}
                          </span>
                        </div>

                        <div className="flex w-full items-center justify-between">
                          <span className="text-body font-body text-subtext-color">
                            {knockOffOpeningBalance
                              ? "Opening balance"
                              : "Advance Payment"}
                          </span>
                          <span className="text-body-bold font-body-bold text-default-font">
                            {hasAnyForeign &&
                            firstBankExchangeRate &&
                            firstBankExchangeRate > 0
                              ? `${currencySymbol}${formatAmount(
                                  Math.max(
                                    0,
                                    knockOffOpeningBalance
                                      ? totalOnAccountAmounts
                                      : totalAdvancePayments,
                                  ),
                                )}`
                              : `₹${formatAmount(
                                  Math.max(
                                    0,
                                    knockOffOpeningBalance
                                      ? totalOnAccountAmounts
                                      : totalAdvancePayments,
                                  ),
                                )}`}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </div>
        {/* Activity Logs */}

        <div className="flex w-full flex-col gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <div className="flex items-center justify-between">
            <span className="text-heading-4 font-heading-4 text-default-font">
              Activity Logs
            </span>
          </div>
          {isLoadingActivityLogs ? (
            <div className="flex w-full items-center justify-center py-4 text-body text-subtext-color">
              Loading activity logs...
            </div>
          ) : activityLogs.length === 0 ? (
            <span className="text-body text-subtext-color">
              No activity logs available.
            </span>
          ) : (
            <div className="flex flex-col gap-3">
              {activityLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex flex-col gap-1 rounded border border-neutral-border px-4 py-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-body-bold text-default-font">
                      {log.activity_name}
                    </span>
                    <span className="text-caption text-subtext-color">
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  </div>
                  <span className="text-caption text-subtext-color">
                    By {log.user_name}
                  </span>
                  <span className="text-body text-default-font">
                    {log.description}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex w-full items-center justify-between border-t border-solid border-neutral-border pt-6">
          {/* <Button
            variant="neutral-tertiary"
            icon={<FeatherFlag />}
            onClick={handleFlagForReview}
          >
            Flag for Review
          </Button> */}
          <div className="flex items-center gap-2">
            <Button variant="neutral-tertiary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleMatch}
              disabled={
                isInitialLoading ||
                hasValidationErrors() ||
                isMatching ||
                reviewStatus === "pending" ||
                isViewOnlyMode()
              }
              loading={isInitialLoading || isMatching}
              className={
                isInitialLoading ||
                hasValidationErrors() ||
                reviewStatus === "pending" ||
                isViewOnlyMode()
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }
            >
              Match
            </Button>
          </div>
        </div>
      </div>

      {/* Closed month confirm modal - when non-Admin/CFO tries to match in closed month */}
      <ClosedMonthConfirmModal
        open={showClosedMonthConfirmModal}
        onOpenChange={setShowClosedMonthConfirmModal}
        entityLabel="Bank transaction"
        onConfirm={() => {
          setShowClosedMonthConfirmModal(false);
          const requestBody = buildRequestBodyForCategory(selectedCategory);
          const oldData = buildEmptyOldDataForCategory(selectedCategory);
          const fieldChanges = buildFieldChangesForCategory(
            selectedCategory,
            oldData,
            requestBody,
          );
          setPendingMatchAction({
            action: async () => {
              await handleMatch();
            },
            changeDetails: {
              closed_month: true,
              action_types: ["match", ACTION_TYPE_MONTH_CLOSE],
              field_changes: fieldChanges,
              raw_json: requestBody,
            },
            actionCode: ACTION_CODES.MATCH,
          });
          setIsReviewModalOpen(true);
        }}
      />

      {/* Review Request Modal */}
      {transactionData?.id && pendingMatchAction && (
        <ReviewRequestModal
          open={isReviewModalOpen}
          onOpenChange={setIsReviewModalOpen}
          moduleCode={MODULE_CODES.BANK_TRANSACTION}
          actionCode={pendingMatchAction.actionCode || ACTION_CODES.MATCH}
          entityType="bank_transaction"
          entityId={transactionData.id}
          changeDetails={pendingMatchAction.changeDetails}
          transactionData={transactionData}
          skipNavigation={true}
          onSuccess={async () => {
            // ReviewRequestModal already showed success toast ("Review request submitted successfully")

            // Close the review modal (already handled by ReviewRequestModal)
            setIsReviewModalOpen(false);

            // Close the transaction modal
            handleClose();

            // Only refresh list so "Sent for Review" shows; do NOT call onTransactionMatched (that would show "reconciled successfully")
            onReviewRequestSubmitted?.();
          }}
          onError={(error) => {
            console.error("Failed to create review request:", error);
            setApiErrors(error);
          }}
        />
      )}
    </DialogLayout>
  );
};

export default CustomerPaymentTransactionModal;
